<template>
<div class="chapter">
<div class="inner_page">
<p id="id00002">   "Bartimeus" is the pseudonym of Captain Lewis Ritchie, R.N.</p>
<h1 id="id00003" style="margin-top: 5em">A TALL SHIP</h1>
<p id="id00004">On Other Naval Occasions</p>
<p id="id00005">by</p>
<h5 id="id00006">"BARTIMEUS"</h5>
<p id="id00007">Author of "Naval Occasions"</p>
<p id="id00008" style="margin-top: 7em">  . . . "All I ask is a tall ship and a star to steer her by,<br/>

        *      *      *<br/>

  And a laughing yarn from a merry fellow rover,<br/>

  And a quiet sleep and a sweet dream when the long trick's over."<br/>

        JOHN MASEFIELD<br/>
</p>
<p id="id00009" style="margin-top: 4em">Cassell and Company, Ltd<br/>

London, New York, Toronto and Melbourne<br/>
</p>
<p id="id00010">First published September 1915.<br/>

Reprinted September and October 1915.<br/>
</p>
<p id="id00011" style="margin-top: 4em">To</p>
<h5 id="id00012">H. M. S.</h5>
<h2 id="id00013" style="margin-top: 4em">PREFACE</h2>
<p id="id00014">It is almost superfluous to observe that the following sketches contain
no attempt at the portrait of an individual.  The majority are etched
in with the ink of pure imagination.  A few are "composite" sketches of
a large number of originals with whom the Author has been shipmates in
the past and whose friendship he is grateful to remember.</p>
<p id="id00015">Of these, some, alas! have finished "the long trick."  To them, at no
risk of breaking their quiet sleep—<i>Ave atque vale</i>.</p>
<p id="id00016">"Crab-Pots," "The Day," and "Chummy-Ships" appeared originally in
<i>Blackwood's Magazine</i>, and are reproduced here by kind permission of
the Editor.</p>
<h2 id="id00017" style="margin-top: 4em">CONTENTS</h2>
<h4 id="id00018" style="margin-top: 2em">  1. CRAB-POTS
  2. THE DRUM
  3. A CAPTAIN'S FORENOON
  4. THE SEVEN-BELL BOAT
  5. THE KING'S PARDON
  6. AN OFF-SHORE WIND
  7. THE DAY
  8. THE MUMMERS
  9. CHUMMY-SHIPS
 10. THE HIGHER CLAIM</h4>
<h2 id="id00019" style="margin-top: 4em">A TALL SHIP</h2>
<h4 id="id00020" style="margin-top: 2em">I</h4>
<h5 id="id00021">CRAB-POTS</h5>
<p id="id00022">1</p>
<p id="id00023">In moments of crisis the disciplined human mind works as a thing
detached, refusing to be hurried or flustered by outward circumstance.
Time and its artificial divisions it does not acknowledge.  It is
concerned with preposterous details and with the ludicrous, and it is
acutely solicitous of other people's welfare, whilst working at a speed
mere electricity could never attain.</p>
<p id="id00024">Thus with James Thorogood, Lieutenant, Royal Navy, when he—together
with his bath, bedding, clothes, and scanty cabin furniture, revolver,
first-aid outfit, and all the things that were his—was precipitated
through his cabin door across the aft-deck.  The ship heeled violently,
and the stunning sound of the explosion died away amid the uproar of
men's voices along the mess-deck and the tinkle and clatter of broken
crockery in the wardroom pantry.</p>
<p id="id00025">"Torpedoed!" said James, and was in his conjecture entirely correct.
He emerged from beneath the debris of his possessions, shaken and
bruised, and was aware that the aft-deck (that spacious vestibule
giving admittance on either side to officers' cabins, and normally
occupied by a solitary Marine sentry) was filled with figures rushing
past him towards the hatchway.</p>
<p id="id00026">It was half-past seven in the morning.  The Morning-watch had been
relieved and were dressing.  The Middle-watch, of which James had been
one, were turning out after a brief three-hours' spell of sleep.
Officers from the bathroom, girt in towels, wardroom servants who had
been laying the table for breakfast, one or two Warrant-officers in sea
boots and monkey jackets—the Watch-below, in short—appeared and
vanished from his field of vision like figures on a screen.  In no
sense of the word, however, did the rush resemble a panic.  The
aft-deck had seen greater haste on all sides in a scramble on deck to
cheer a troopship passing the cruiser's escort.  But the variety of
dress and undress, the expressions of grim anticipation in each man's
face as he stumbled over the uneven deck, set Thorogood's reeling mind,
as it were, upon its feet.</p>
<p id="id00027">The Surgeon, pyjama clad, a crimson streak running diagonally across
the lather on his cheek, suddenly appeared crawling on all-fours
through the doorway of his shattered cabin.  "I always said those
safety-razors were rotten things," he observed ruefully.  "I've just
carved my initials on my face.  And my ankle's broken.  Have we been
torpedoed, or what, at all?  An' what game is it you're playing under
that bath, James?  Are you pretending to be an oyster?"</p>
<p id="id00028">Thorogood pulled himself together and stood up.  "I think one of their
submarines must have bagged us."  He nodded across the flat to where,
beyond the wrecked debris of three cabins, the cruiser's side gaped
open to a clear sky and a line of splashing waves.  Overhead on deck
the twelve-pounders were barking out a series of ear-splitting
reports—much as a terrier might yap defiance at a cobra over the
stricken body of its master.</p>
<p id="id00029">"I think our number's up, old thing."  Thorogood bent and slipped his
arms under the surgeon's body.  "Shove your arms round my neck. . . .
Steady!—hurt you?  Heave!  Up we go!"  A Midshipman, ascending the
hatchway, paused and turned back.  Then he ran towards them, spattering
through the water that had already invaded the flat.</p>
<p id="id00030">"Still!" sang a bugle on deck.  There was an instant's lull in the
stampede of feet overhead.  The voices of the officers calling orders
were silent.  The only sounds were the lapping of the waves along the
riven hull and the intermittent reports of the quick-firers.  Then came
the shrill squeal of the pipes.</p>
<p id="id00031">"Fall in!" roared a voice down the hatchway.  "Clear lower deck!  Every
soul on deck!"  The bugle rang out again.</p>
<p id="id00032">Thorogood staggered with his burden across the buckled plating of the
flat, and reached the hatchway.  The Midshipman who had turned back
passed him, his face white and set.  "Here!" called the Lieutenant from
the bottom of the ladder.  "This way, my son!  Fall in's the order!"
For a moment the boy glanced back irresolute across the flat, now ankle
deep in water.  The electric light had been extinguished, and in the
greenish gloom between decks he looked a small and very forlorn figure.
He pointed towards the wreckage of the after-cabin, called something
inaudible, and, turning, was lost to view aft.</p>
<p id="id00033">"That's the 'Pay's' cabin," said the Doctor between his teeth.  "He was
a good friend to that little lad.  I suppose the boy's gone to look for
him, and the 'Pay' as dead as a haddock, likely as not."</p>
<p id="id00034">Thorogood deposited the Surgeon on the upper deck, fetched a lifebuoy,
and rammed it over the injured man's shoulders.  "God forgive me for
taking it," said the latter gratefully, "but my fibula's cracked to
blazes, an' I love my wife . . ."</p>
<p id="id00035">All round them men were working furiously with knives and crowbars,
casting off lashings from boats and baulks of timber on the booms,
wrenching doors and woodwork from their fastenings—anything capable of
floating and supporting a swimmer.  The officers were encouraging the
men with words and example, steadying them with cheery catch-words of
their Service, ever with an eye on the forebridge, at the extreme end
of which the Captain was standing.</p>
<p id="id00036">On the after shelter-deck the Gunner, bare-headed and clad only in a
shirt and trousers, was, single-handed, loading and firing a
twelve-pounder as fast as he could snap the breech to and lay the gun.
His face was distorted with rage, and his black brows met across his
nose in a scowl that at any other time would have suggested acute
melodrama.  Half a mile away the shots were striking the water with
little pillars of white spray.</p>
<p id="id00037">The figure on the forebridge made a gesture with his arm.  "Fall in!"
shouted the Commander.  "Fall in, facing outboard, and strip!  Stand by
to swim for it!"  Seven hundred men—bluejackets, stokers, and
marines—hurriedly formed up and began to divest themselves of their
clothes.  They were drawn up regardless of class or rating, and a burly
Marine Artilleryman, wriggling out of his cholera belt, laughed in the
blackened face of a stoker fresh from the furnace door.</p>
<p id="id00038">"Cheer up, mate!" he said encouragingly.  "You'll soon 'ave a chance to
wash your bloomin' face!"</p>
<p id="id00039">The ship gave a sudden lurch, settled deeper in the water, and began to
heel slowly over.  The Captain, clinging to the bridge rail to maintain
his balance, raised the megaphone to his mouth:</p>
<p id="id00040">"Carry on!" he shouted.  "Every man for himself!"—he lowered the
megaphone and added between his teeth—"and God for us all!"</p>
<p id="id00041">The ship was lying over at an angle of sixty degrees, and the men were
clustered along the bulwarks and nettings as if loath to leave their
stricken home even at the eleventh hour.  A muscular Leading Seaman was
the first to go—a nude, pink figure, wading reluctantly down the
sloping side of the cruiser, for all the world like a child paddling.
He stopped when waist deep and looked back.  "'Ere!" he shouted, "'ow
far is it to Yarmouth?  No more'n a 'undred an' fifty miles, is it?  I
gotter aunt livin' there. . . ."</p>
<p id="id00042">Then came the rush, together with a roar of voices, shouts and cheers,
cries for help, valiant, quickly stifled snatches of "Tipperary," and,
over all, the hiss of escaping steam.</p>
<p id="id00043">"She wouldn't be 'arf pleased to see yer, Nobby!" shouted a voice above
the hubbub.  "Not 'arf she wouldn't!  Nah then, 'oo's for compulsory
bathin'. . . .  Gawd! ain't it cold! . . ."</p>
<p id="id00044">      *      *      *      *      *</p>
<p id="id00045">How he found himself in the water, Thorogood had no very clear
recollection; but by instinct he struck out through the welter of
gasping, bobbing heads till he was clear of the clutching menace of the
drowning.  The Commander, clad simply in his wrist-watch and uniform
cap, was standing on the balsa raft, with scores of men hanging to its
support.  "Get away from the ship!" he was bawling at the full strength
of his lungs.  "Get clear before she goes——!"</p>
<p id="id00046">The stern of the cruiser rose high in the air, and she dived with
sickening suddenness into the grey vortex of waters.  Pitiful cries for
help sounded on all sides.  Two cutters and a few hastily constructed
rafts were piled with survivors; others swam to and fro, looking for
floating debris, or floated, reserving their strength.</p>
<p id="id00047">The cries and shouts grew fewer.</p>
<p id="id00048">Thorogood had long parted with his support—the broken loom of an
oar—and was floating on his back, when he found himself in close
proximity to two figures clinging to an empty breaker.  One he
recognised as a Midshipman, the other was a bearded Chief Stoker.  The
boy's teeth were chattering and his face was blue with cold.</p>
<p id="id00049">"W-w-what were you g-g-g-oing to have for b-b-b-breakfast in your
m-m-mess?" he was asking his companion in misfortune.</p>
<p id="id00050">Hang it all, a fellow of fifteen had to show somehow he wasn't afraid
of dying.</p>
<p id="id00051">"Kippers," replied the Chief Stoker, recognising his part and playing
up to it manfully.  "I'm partial to a kipper, meself—an' fat
'am. . . ."</p>
<p id="id00052">The Midshipman caught sight of Thorogood, and raised an arm in
greeting.  As he did so a sudden spasm of cramp twisted his face like a
mask.  He relaxed his grasp of the breaker and sank instantly.</p>
<p id="id00053">The two men reappeared half a minute later empty handed, and clung to
the barrel exhausted.</p>
<p id="id00054">"It's all chalked up somewhere, I suppose," spluttered James, gasping
for his breath.</p>
<p id="id00055">"Child murder, sir, I reckon that is," was the tense reply.  "That's on
their slop ticket all right. . . .  'Kippers,' I sez, skylarkin'
like . . . an' 'e sinks like a stone. . . ."</p>
<p id="id00056">Among the wavetops six hundred yards away a slender, upright object
turned in a wide circle and moved slowly northward.  To the south a
cluster of smoke spirals appeared above the horizon, growing gradually
more distinct.  The party in one of the cutters raised a wavering cheer.</p>
<p id="id00057">"Cheer up for Chatham!" shouted a clear voice across the grey waste of
water.  "Here come the destroyers!  . . .  Stick it, my hearties!"</p>
<p id="id00058">      *      *      *      *      *</p>
<p id="id00059">After a month's leave James consulted a specialist.  He was a very wise
man, and his jerky discourse concerned shocked nerve-centres and reflex
actions.  "That's all right," interrupted the thoroughly startled James
(sometime wing three-quarter for the United Services XV.), "but what
defeats me is not being able to cross a London street without 'coming
over all of a tremble'!  An' when I try to light a cigarette"—he
extended an unsteady hand—"look! . . .  I'm as fit as a fiddle,
really.  Only the Medical Department won't pass me for service afloat.
An' I want to get back, d'you see?  There's a super-Dreadnought
commissioning soon——"</p>
<p id="id00060">The specialist wrote cabalistic signs on a piece of paper.  "Bracing
climate—East Coast for preference. . . .  Plenty of exercise.  Walk.
Fresh air.  Early hours.  Come and see me again in a fortnight, and get
this made up.  That's all right"—he waved aside James's proffered
guineas.  "Don't accept fees from naval or military. . . .  Least we
can do is to mend you quickly.  'Morning. . . ."</p>
<p id="id00061">James descended the staircase, and passed a tall, lean figure in soiled
khaki ascending, whom the public (together with his wife and family)
had every reason to suppose was at that moment in the neighbourhood of
Ypres.</p>
<p id="id00062">"If it weren't for those fellows I couldn't be here," was his greeting
to the specialist.  He jerked his grey, close-cropped head towards the
door through which Thorogood had just passed.</p>
<p id="id00063" style="margin-top: 3em">2</p>
<p id="id00064">A ramshackle covered cart laden with an assortment of tinware had
stopped on the outskirts of the village.  The owner, a bent scarecrow
of a fellow, was effecting repairs to his nag's harness with a piece of
string.  Evening was setting in, and the south-east wind swept a grey
haze across the coast road and sombre marshes.  The tinker completed
first-aid to the harness, and stood at the front of the cart to light
his lamps.  The first match blew out, and he came closer to the body of
the vehicle for shelter from the wind.</p>
<p id="id00065">At that moment a pedestrian passed, humming a little tune to himself,
striding along through the November murk with swinging gait.  It may
have been that his voice, coming suddenly within range of the mare's
ears, conveyed a sound of encouragement.  Perhaps the lights of the
village, twinkling out one by one along the street, suggested stables
and a nosebag.  Anyhow, the tinker's nag threw her weight suddenly into
the collar, the wheel of the cart passed over the tinker's toe, and the
tinker uttered a sudden exclamation.</p>
<p id="id00066">In the circumstances it was a pardonable enough ebullition of feeling
and ought not to have caused the passing pedestrian to spin round on
his heel, astonishment on every line of his face.  The next moment,
however, he recovered himself.  "Did you call out to me?" he shouted.</p>
<p id="id00067">The tinker was nursing his toe, apparently unconscious of having given
anyone more food for thought than usual.  "No," he replied gruffly.  "I
'urt myself."</p>
<p id="id00068">The passer-by turned and pursued his way to the village.  The tinker
lit his lamps and followed.  He was a retiring sort of tinker, and
employed no flamboyant methods to advertise his wares.  He jingled
through the village without attracting any customers—or apparently
desiring to attract any—and followed the sandy coast road for some
miles.</p>
<p id="id00069">At length he pulled up, and from his seat on the off-shaft sat
motionless for a minute, listening.  The horse, as if realising that
its dreams of a warm stable were dreams indeed, hung its head
dejectedly, and in the faint gleam, of the lamp its breath rose in thin
vapour.  The man descended from his perch on the shaft and, going to
his nag's head, turned the cart off the road.</p>
<p id="id00070">For some minutes the man and horse stumbled through the darkness; the
cart jolted, and the tin merchandise rattled dolefully.  The tinker,
true to the traditions of his calling, swore again.  Then he found what
he had been looking for, an uneven track that wound among the
sand-dunes towards the shore.  The murmur of the sea became suddenly
loud and distinct.</p>
<p id="id00071">With a jerk the horse and cart came to a standstill.  In a leisurely
fashion the tinker unharnessed his mare, tied a nosebag on her, and
tethered her to the tail of the cart.  In the same deliberate manner he
rummaged about among his wares till he produced a bundle of sticks and
some pieces of turf.  With these under his arm, he scrambled off across
the sand-hills to the sea.</p>
<p id="id00072">The incoming tide sobbed and gurgled along miniature headlands of rock
that stretched out on either side of a little bay.  The sand-hills
straggled down almost to high-water mark, where the winter storms had
piled a barrier of kelp and debris.  At one place a rough track down to
the shingle had been worn in the sand by the feet of fishermen using
the cove in fine weather during the summer.</p>
<p id="id00073">The tinker selected a site for his fire in a hollow that opened to the
sea.  He built a hearth with flat stones, fetched a kettle from the
cart, kindled the fire, and busied himself with preparations for his
evening meal.  This concluded, he laid a fresh turf of peat upon the
embers, banked the sand up all round till the faint glow was invisible
a few yards distant, and lit a pipe.</p>
<p id="id00074">The night wore on.  Every now and again the man rose, climbed a
sand-hill, and stood listening, returning each time to his vigil by the
fire.  At length he leaned forward and held the face of his watch near
the fire-glow.  Apparently the time had come for action of some sort,
for he rose and made off into the darkness.  When he reappeared he
carried a tin pannikin in his hand, and stood motionless by the fire,
staring out to sea.</p>
<p id="id00075">Ten minutes he waited; then, suddenly, he made an inaudible
observation.  A light appeared out of the darkness beyond the headland,
winked twice, and vanished.  The tinker approached his fire and swilled
something from his pannikin on to the glowing embers.  A flame shot up
about three feet, and died down, flickering.  The tin contained
paraffin, and three times the tinker repeated the strange rite.  Then
he sat down and waited.</p>
<p id="id00076">A quarter of an hour passed before something grated on the shingle of
the beach, scarcely perceptible above the lap of the waves.  The tinker
rose to his feet, shovelled the sand over the embers of his fire, and
descended the little path to the beach.  The night was inky dark, and
for a moment he paused irresolute.  Then a dark form appeared against
the faintly luminous foam, wading knee deep and dragging the bows of a
small skiff towards the shore.  The tinker gave a low whistle, and the
wader paused.</p>
<p id="id00077">"<i>Fritz!</i>" he said guardedly.</p>
<p id="id00078">"<i>Ja!  Hier!</i>" replied the tinker, advancing.</p>
<p id="id00079">"<i>Gott sei dank!</i>" said the other.  He left the boat and waded ashore.
The two men shook hands.  "Where's the cart?" asked the low voice in
German.</p>
<p id="id00080">"Among the sand-hills.  You will want assistance.  Have you more than
one with you in the boat?"</p>
<p id="id00081">"Yes."  The new-comer turned and gave a brusque order.  Another figure
waded ashore and joined the two men, a tall, bearded fellow in duffel
overalls.  As his feet reached the sand he spat ostentatiously.  The
tinker led the way to the cart.</p>
<p id="id00082">"It is dark," said the first man from the sea.  "How many cans have you
got?"</p>
<p id="id00083">"Forty-eight.  I could get no more without exciting suspicion.  They
have requisitioned one of my cars as it is."</p>
<p id="id00084">The other gave a low laugh.  "What irony!  Well, that will last till
Friday.  But you must try and get more then.  I will be here at the
same time; no, the tide will not suit—at 8 a.m.  We can come inside
then.  Did you remember the cigarettes?"</p>
<p id="id00085">"Yes."  The tinker climbed into the cart and handed a petrol tin down
to the speaker.  "<i>Ein!</i>" he said.  "Count them," and lifted out
another.  "<i>Zwei!</i>"  The third man, who had not hitherto spoken,
received them with a grunt, and set off down to the boat with his
burden.</p>
<p id="id00086">Eight times the trio made the journey to and from the beach.  Three
times they waited while the tiny collapsible boat ferried its cargo out
to where, in the darkness, a long, black shadow lay, with the water
lapping round it, like a partly submerged whale.  The last time the
tinker remained alone on the beach.</p>
<p id="id00087">He stood awhile staring out into the darkness, and at length turned to
retrace his steps.  As he reached the shelter of the sand-dunes a tall
shadow rose out of the ground at his feet, and the next instant he was
writhing on his face in the grip of an exceedingly effective
neck-and-arm lock.</p>
<p id="id00088">"If you try to kick, my pippin," said the excited voice of James<br/>

Thorogood, "I shall simply break your arm—so!"<br/>
</p>
<p id="id00089">The face in the sand emitted a muffled squark.</p>
<p id="id00090">"Keep still, then."</p>
<p id="id00091">The two men breathed heavily for a minute.</p>
<p id="id00092">"Don't swear, either.  That's what got you into this trouble, that
deplorable habit of swearing aloud in German.  But I will say, for a
tinker, you put a very neat West Country whipping on that bit of broken
harness.  I've been admiring it.  Didn't know they taught you that in
the German navy—<i>don't</i> wriggle."</p>
<p id="id00093" style="margin-top: 3em">3</p>
<p id="id00094">James Thorogood, retaining a firm hold on his companion's arm, bent
down and gathered a handful of loose earth from a flower-bed at his
feet.  The moonlight, shining fitfully through flying clouds, illumined
the face of the old house and the two road-stained figures standing
under its walls.  It was a lonely, rambling building, partly sheltered
from the prevailing wind by a clump of poplars, and looking out down an
avenue bordered by untidy rhododendrons.</p>
<p id="id00095">"Won't Uncle Bill be pleased!" said James, and flung his handful of
earth with relish against one of the window-panes on the first floor.
He and his captive waited in silence for some minutes; then he repeated
the assault.  Soon a light wavered behind the curtains, the sash
lifted, and a head and shoulders appeared.</p>
<p id="id00096">"Hallo!" said a man's voice.</p>
<p id="id00097">"Uncle Bill!" called James.  There was a moment's silence.</p>
<p id="id00098">"Well?" said the voice again, patiently.</p>
<p id="id00099">"Uncle Bill!  It's me—Jim.  Will you come down and open the door?  And
don't wake Janet, whatever you do."  Janet was the housekeeper, stone
deaf these fifteen years.</p>
<p id="id00100">The head and shoulders disappeared.  Again the light flickered, grew
dim, and vanished.  "This way," said James, and led his companion round
an angle of the house into the shadow of the square Georgian porch.
The bolts were being withdrawn as they reached the steps, and a tall,
grey-haired man in a dressing-gown opened the door.  He held a candle
above his head and surveyed the wayfarers through a rimless monocle.</p>
<p id="id00101">"Didn't expect you till to-morrow," was his laconic greeting.  "Brought
a friend?"</p>
<p id="id00102">"He's not a friend exactly," said James, pushing his companion in
through the door, and examining him curiously by the light of the
candle.  "But I'll tell you all about him later on.  His name's Fritz.
D'you mind if I lock him in the cellar?"</p>
<p id="id00103">"Do," replied Uncle Bill dryly.  He produced a bunch of keys from the
pocket of his dressing-gown.  "It's the thin brass key.  There's some
quite decent brandy in the farthest bin on the right-hand side, if
you're thinking of making a night of it down there.  Take the candle;
I'm going back to bed."</p>
<p id="id00104">"Don't go to bed," called James from the head of the stairs.  "I want
to have a yarn with you in a minute.  Light the gas in the dining-room."</p>
<p id="id00105">Five minutes later he reappeared carrying a tray with cold beef, bread,
and a jug of beer upon it.  Uncle Bill stood in front of the dead ashes
of his hearth considering his nephew through his eyeglass.  "I hope you
made—er—Fritz comfortable?  You look as if you had been doing a
forced march.  Nerves better?"</p>
<p id="id00106">James set down his empty glass with a sigh and wiped his mouth.  "As
comfortable as he deserves to be.  He's a spy, Uncle Bill.  I caught
him supplying petrol to a German submarine."</p>
<p id="id00107">"Really?" said Uncle Bill, without enthusiasm.  "That brandy cost me
180s. a dozen.  Wouldn't he be better in a police station?  Have you
informed the Admiralty?"</p>
<p id="id00108">"I venerate the police," replied James flippantly, "and the Admiralty
are as a father and mother to me; but I want to keep this absolutely
quiet for a few days—anyhow, till after Friday.  I couldn't turn Fritz
over to a policeman without attracting a certain amount of attention.
Anyhow, it would leak out if I did.  I've walked eighteen miles already
since midnight, and it's another fifty-nine to the Admiralty from here.
Besides, unless I disguise Fritz as a performing bear, people would
want to know why I was leading him about on a rope's end——"</p>
<p id="id00109">"Start at the beginning," interrupted Uncle Bill wearily, "and explain,
avoiding all unnecessary detail."</p>
<p id="id00110">So James, between mouthfuls, gave a brief résumé of the night's
adventure, while Sir William Thorogood, Professor of Chemistry and
Adviser to the Admiralty on Submarine Explosives, stood and shivered on
the hearthrug.</p>
<p id="id00111">"And it just shows," concluded his nephew, "what a three-hours' swim in
the North Sea does for a chap's morals."  He eyed his Uncle Bill
solemnly.  "I even chucked the fellow's seamanship in his teeth!"</p>
<p id="id00112">Sir William polished his eyeglass with a silk handkerchief and replaced
it with care.</p>
<p id="id00113">"<i>Did</i> you!" he said.</p>
<p id="id00114" style="margin-top: 3em">4</p>
<p id="id00115">A squat tub of a boat, her stern piled high with wicker crab-pots, came
round the northern headland and entered the little bay.  The elderly
fisherman who was rowing rested on his oars and sat contemplating the
crab-pots in the stem.  A younger man, clad in a jersey and sea boots,
was busy coiling down something in the bows.  "How about this spot," he
said presently, looking up over his shoulder, "for the first one?"  The
rower fumbled about inside his tattered jacket, produced something that
glistened in the sunlight, and screwed it into his eye.</p>
<p id="id00116">"Uncle Bill!" protested the younger fisherman, "do unship that thing.<br/>

If there <i>is</i> anyone watching us, it will give the whole show away."<br/>
</p>
<p id="id00117">Sir William Thorogood surveyed the harbour with an expressionless
countenance.  "I consider that having donned these unsavoury
garments—did Janet bake them thoroughly, by the way?—I have already
forfeited my self-respect quite sufficiently.  How much of the circuit
have you got off the drum?"</p>
<p id="id00118">"Six fathoms."</p>
<p id="id00119">"That's enough for the first, then."  The speaker rose, lifted a
crab-pot with an effort, and tipped it over the side of the boat.  The
cable whizzed out over the gunwale for a few seconds and stopped.
Uncle Bill resumed paddling for a little distance, and repeated the
manoeuvre eight times in a semi-circle round the inside of the bay,
across the entrance.  "That's enough," he observed at length, as the
last crab-pot sank with a splash.  "Don't want to break all their
windows ashore.  These will do all they're intended to."  He propelled
the boat towards the shore, while James paid out the weighted cable.
The bows of the boat grated on the shingle, and the elder man climbed
out.  "Hand me the battery and the firing key—in that box under the
thwart there.  Now bring the end of the cable along."</p>
<p id="id00120">As they toiled up the shifting flank of a sand-dune, James indicated a
charred spot in the sand.  "That's where he showed the flare, Uncle
Bill."</p>
<p id="id00121">Uncle Bill nodded disinterestedly.  Side by side they topped the tufted
crest of the dune and vanished among the sand-hills.</p>
<p id="id00122">      *      *      *      *      *</p>
<p id="id00123">Somewhere across the marshes a church clock was striking midnight when
a big covered car pulled up at the roadside in the spot where, a few
nights before, the tinker's cart had turned off among the sand-hills.
The driver switched the engine off and extinguished the lights.  Two
men emerged from the body of the car; one, a short, thick-set figure
muffled in a Naval overcoat, stamped up and down to restore his
circulation.  "Is this the place?" he asked.</p>
<p id="id00124">"Part of it," replied the voice of Uncle Bill from the driving seat.
"My nephew will show you the rest.  I shall stay here, if Jim doesn't
mind handing me the Thermos flask and my cigar-case—thanks."</p>
<p id="id00125">James walked round the rear of the car and began groping about in the
dry ditch at the roadside.</p>
<p id="id00126">"Don't say you can't find it, Jim," said Sir William.  He bent forward
to light his cigar, and the flare of the match shone on his dress
shirt-front and immaculate white tie.  He refastened his motoring coat,
and leaned back puffing serenely.</p>
<p id="id00127">"Got it!" said a voice from the ditch, and James reappeared, carrying a
small box and trailing something behind him.  He held it out to the
short man with gold oak leaves round his cap-peak.  His hand trembled
slightly.</p>
<p id="id00128">"Here's the firing key, sir!"</p>
<p id="id00129">"Oh, thanks.  Let's put it in the sternsheets of the car till I come
back.  I'd like to have a look at the spot."</p>
<p id="id00130">"You'll get your boots full of sand," said Uncle Bill's voice under the
hood.</p>
<p id="id00131">James lifted a small sack and an oil-can out of the motor, and the two
figures vanished side by side into the night.</p>
<p id="id00132">Half an hour later the elder man reappeared.  "He's going to blow a
whistle," he observed, and climbed into the body of the car, where Sir
William was now sitting under a pile of rugs.  He made room for the
new-comer.</p>
<p id="id00133">"Have some rug . . . and here's the foot-warmer. . . .  I see.  And
then you—er—do the rest?  The box is on the seat beside you."</p>
<p id="id00134">The other settled down into his seat and tucked the rug round himself.
"Thanks," was the grim reply.  "Yes, I'll do the rest!"  He lit a pipe,
and smoked in silence, as if following a train of thought.  "My boy
would have been sixteen to-morrow. . . ."</p>
<p id="id00135">"Ah!" said Uncle Bill.</p>
<p id="id00136">An hour passed.  The Naval man refilled and lit another pipe.  By the
light of the match he examined his watch.  "I suppose you tested the
contacts?" he asked at length in a low voice.</p>
<p id="id00137">"Yes," was the reply, and they lapsed into silence again.  The other
shifted his position slightly and raised his head, staring into the
darkness beyond the road whence came the faint, continuous murmur of
the sea.</p>
<p id="id00138">Seaward a faint gleam of light threw into relief for an instant the
dark outline of a sand-dune, and sank into obscurity again.</p>
<p id="id00139">Uncle Bill's eyeglass dropped against the buttons of his coat with a
tinkle.  The grim, silent man beside him lifted something on to his
knees, and there was a faint click like the safety-catch of a gun being
released.</p>
<p id="id00140">A frog in the ditch near by set up a low, meditative croaking.  Uncle
Bill raised his head abruptly.  Their straining ears caught the sound
of someone running, stumbling along the uneven track that wound in from
the shore.  A whistle cut the stillness like a knife.</p>
<p id="id00141">There was a hoarse rumble seaward that broke into a deafening roar, and
was succeeded by a sound like the bursting of a dam.  The car rocked
with the concussion, and the fragments of the shattered wind-screen
tinkled down over the bonnet and footboard.</p>
<p id="id00142">Then utter, absolute silence.</p>
<h2 id="id00143" style="margin-top: 4em">II</h2>
<h5 id="id00144">THE DRUM</h5>
<p id="id00145">1</p>
<p id="id00146">Ole Jarge put down the baler and wiped the perspiration from his
forehead.  A few fish scales transferred themselves from the back of
his oakum-coloured hand to his venerable brow.</p>
<p id="id00147">"'Tain't no use," he murmured.  "'Er's nigh twenty year' ole—come nex'
month.  Tar ain't no use neither.  'Tis new strakes 'ers wantin'."  He
thumbed the seams of the old boat that lay on the shingle, with the
outgoing tide still lapping round her stern.  "An' new strakes do cost
tarrible lot."  He sat puffing his clay pipe, and transferred his gaze
from the bottom of the boat to the whitewashed cottages huddled under
the lee of the cliffs.  A tall figure was moving about the nets that
festooned the low wall in front of the cottages.</p>
<p id="id00148">Ole Jarge removed his pipe from his mouth, substituted two fingers of
his right hand, and gave a long, shrill whistle.  It was a
disconcerting performance.  For one thing, you associated the trick
with irrepressible boyhood, and, for another, the old man squinted
slightly as he did it.  As a matter of fact, he had learned it on the
Dogger Bank fifty years before; fog-bound in a dory, it was a useful
accomplishment.</p>
<p id="id00149">Young Jarge straightened up, raised one hand in acknowledgment of the
summons, and came crunching slowly across the shingle towards the boat.
Ole Jarge sat smoking in philosophical silence till his son was beside
him.  Then he removed his pipe and spat over the listed gunwale.</p>
<p id="id00150">"'Er's daid," he observed laconically.</p>
<p id="id00151">Young Jarge bent stiffly and tapped the seams, inside and out, much as
a veterinary surgeon runs his hand over a horse's legs.</p>
<p id="id00152">"Ya-a-is," he confirmed, and sat down on the stem of the old boat.
"'Er's very nigh's ole 's what us be," he added, after a pause, and
began shredding some tobacco into the palm of his hand.</p>
<p id="id00153">Ole Jarge nodded.  Then he lifted his head quickly.  "'Er's bound to
last 'nother year."  For the first time there was concern in his voice.
Adversity does not grip the mind of the Cornish fisherfolk suddenly.
It filters slowly through the chinks of the armour God has given them.
Cornish men (and surely Cornish maids) were kind to the survivors of
the wrecked Armada.  It may be that they, in their turn, bequeathed a
strain of Southern fatalism to many of their benefactors.</p>
<p id="id00154">"'Er's bound to," repeated Ole Jarge.  He got ponderously out of the
boat and removed a tattered sou'wester to scratch his head with his
thumbnail—another trick that had survived the adventurous days of the
Dogger Bank.</p>
<p id="id00155">The unfamiliar note of anxiety in his father's voice stirred Young
Jarge.  He rose to his feet with perplexity in his dark eyes,
mechanically pulling up the bleached leather thigh-boots he wore afloat
and ashore, "rainy-come-fine."</p>
<p id="id00156">Inspiration had come, as it does to men of the West once the need is
realised to the full.</p>
<p id="id00157">"Du 'ee mind that there li'l' ole copper boiler—what come out o'
granfer's house when 'er blawed down—back tu '98?" asked Young Jarge
slowly.</p>
<p id="id00158">Ole Jarge nodded.</p>
<p id="id00159">"S'pose us was to hammer 'n out flat like an' nail un down to bottom,
'long wi' oakum an' drop o' white lead—what du 'ee say?"</p>
<p id="id00160">Ole Jarge silently measured the area of the sprunk strakes with the
stumpy thumb and little finger of an outstretched hand.  Then he
puckered his forehead and stared out to sea, apparently making mental
calculations connected with the "li'l' ole copper boiler."</p>
<p id="id00161">"Ya-a-ais."  He replaced the piece of perished tarpaulin that had once
been a sou'-wester on his head, and set off slowly across the shingle
towards the village.  Young Jarge followed, staring at his boots as he
walked.</p>
<p id="id00162">"Us 'll hammer 'n out after tea," said Ole Jarge over his shoulder.<br/>

His great, great, very great grandfather would have said "<i>Mañana!</i>"<br/>
</p>
<p id="id00163">      *      *      *      *      *</p>
<p id="id00164">The setting sun had tipped the dancing wavelets with fire and was
glowing red in each pool left by the receding tide when Ole Jarge
emerged from his cottage door.  In one hand he carried a hammer, and in
the other a tin of white lead.  Young Jarge joined him with a small,
square copper boiler in his arms.</p>
<p id="id00165">"Where'll us put un tu, feyther?"</p>
<p id="id00166">Ole Jarge set off across the beach in the direction of the boat.
"Bring un along!" he commanded in a manner dimly suggestive of a lord
high executioner.</p>
<p id="id00167">Young Jarge followed, and dumped his burden down alongside the boat.</p>
<p id="id00168">"Now!" said Ole Jarge grimly.  He spat on his hands and prepared to
enjoy himself.  Bang! bang! bang-a-bang! bang! went the hammer.  Young
Jarge sat down on the gunwale of the boat and contemplated his parent's
exertions.</p>
<p id="id00169">"It du put Oi in mind of a drum," he said appreciatively.</p>
<p id="id00170" style="margin-top: 3em">2</p>
<p id="id00171">"Now we can talk!"  Margaret settled her back comfortably against a
ridge of turf and closed her eyes for a moment.</p>
<p id="id00172">"Isn't it heavenly up here?  The wind smells of seaweed, and there must
be some shrub or flower——"  She opened her eyes and looked along the
cliffs, "There's something smelling divinely.  Wild broom, is it?"</p>
<p id="id00173">Her gaze travelled along the succession of ragged headlands and
crescents of sand formed by each little bay of the indented coast.  The
coastguard track, a brown thread winding adventurously among the clumps
of gorse at the very edge of the cliffs, drew her eyes farther and
farther to the west.  In the far distance the track dipped sharply over
a headland where the whitewashed coastguard station stood, and was lost
to view.  She turned and smiled at her companion.  "Now we can talk,"
she repeated.</p>
<p id="id00174">Torps, sitting beside her, met her eyes with his grave, gentle smile.
"I'm so glad to see you again," he said, "that I can't think of
anything else to say.  It was nice of you to write and tell me you were
here."</p>
<p id="id00175">As if by common consent, they had discussed nothing but generalities
during the half-hour's walk that brought them to this sheltered hollow
in the cliffs.  The woman was, of the two, the more reluctant to bridge
the years that lay between to-day and their last meeting.  Yet,
womanlike, it was she that spoke first.</p>
<p id="id00176">"I knew your ship was quite close.  I wanted to see you again, Trevor,
after all these years.  Tell me about yourself.  Your letters—yes, I
know; but you never talked much about yourself in your letters."</p>
<p id="id00177">He shook his head quietly.  "No, you tell first."</p>
<p id="id00178">"There isn't much to tell."  She interlaced her fingers round her
updrawn knees.  Her grey eyes were turned to the sea, and Torps watched
her profile against the sky wistfully, studying the pure brow, the
threads of silver appearing here and there in her soft brown hair, the
strong, almost boyish lines of mouth and chin.  <i>En profile</i>, thus, she
looked very like a handsome boy.</p>
<p id="id00179">"I've been teaching at one of those training institutes for girls on
the East Coast.  The principal, Miss Dacre is her name"—Margaret
paused as if expecting some comment from her companion: none
came—"Pauline Dacre; she was at school with mother: they were great
friends; and when mother died she offered me a home. . . .  I had a
little money—enough to go through a course of training.  I learned
things——"</p>
<p id="id00180">"What sort of things?"</p>
<p id="id00181">"Oh, cooking and laundry, and hygiene—domestic science it's called."
Torps nodded.  "And then, when I knew enough to teach others, I went
to—to this place; I've been there ever since.  And that's all.  Now
it's your turn."</p>
<p id="id00182">Torps studied the traces of overwork and strain which showed in the
faintly accentuated cheekbones and which painted little tired shadows
about her eyelids.</p>
<p id="id00183">"No, it's not all.  Why have you come down here?"</p>
<p id="id00184">"I—I——"  She coloured as if accused.  "I got a little run down . . .
that was all.  But I've saved some money; I can afford a rest.  I'm
what is called 'an independent gentlewoman of leisure' for a while."
She laughed, a gay little laugh.</p>
<p id="id00185">"Do you mean you are going back there again?"</p>
<p id="id00186">She looked at him with frank surprise.  "Of course I am, silly!"</p>
<p id="id00187">"Don't go back . . . not to that life again.  How can you?  Shut up in
a sort of convent. . . .  You can't be a school-marm all your life; you
were meant for other things. . . .  I suppose you have to sleep on a
hard bed, and get up in the dark when a bell rings.  There aren't any
carpets, and they don't give you enough to eat, as likely as not.
Margaret, why should you?  It's the sort of work anyone can do-teaching
kids to mangle."</p>
<p id="id00188">"But . . . what do you think I am going to do with the remainder of my
days—crochet? embroider slippers for the curate?  Trevor, you wouldn't
like me to come to that in my old age, would you?"  She spoke with
gentle banter, as if to fend off something she feared.  Had Torps known
it, she was fencing for the happiness of them both.</p>
<p id="id00189">He shook his head gravely.</p>
<p id="id00190">"I hoped—because you had written to me—that you weren't going
back. . . ."  His thin, strong hand closed over hers, resting on the
turf between them.  He bent his head as if considering their fingers.
"Margaret, dear——"</p>
<p id="id00191">"Ah, Trevor, don't—please don't. . . .  Not again.  I thought all that
was dead and buried years ago.  And do you really think"—she smiled a
little sadly—"if I—if things were different—that I should have
written to ask you to meet me to-day?  Have you learned so little of
women in all these years?"  There was something besides sadness in her
eyes now: a wistful, half-maternal tenderness.  He raised his head.</p>
<p id="id00192">"I've learned nothing about women, Margaret, but what I learned from
you."</p>
<p id="id00193">She gently withdrew her hand.  "Trevor, we're not children any longer.<br/>

We're older and wiser.  We——"<br/>
</p>
<p id="id00194">"We're older—yes.  But I don't see what that has to do with it, except
that my need is greater. . . .  I'm a little lonelier.  There's never
been anyone but you.  I've never looked across the road at a woman in
my life—except you.  I know we're not children, and for that reason we
ought to know our own minds.  Do you know yours, Margaret?"</p>
<p id="id00195">Margaret bowed her head, collecting her thoughts and setting them in
order, before she answered:</p>
<p id="id00196">"It isn't easy to say what I have to say.  You must be
patient—generous, as you can be, Trevor, of all the men I know."  She
hesitated and coloured again a little.  "You say you want me.  If there
were no one else who I thought had a greater claim, you should—no,
hush! listen, dear—I would give you—what you want . . . gladly—oh,
gladly!  But the children need me—my influence. . . .  Miss Dacre said
it is doing the highest service one could for the Empire . . . theirs
is the higher claim.  Can you understand?  Oh, can you?"</p>
<p id="id00197">Torps made no reply, staring out to sea with sombre eyes.</p>
<p id="id00198">Gaining confidence with his silence, she continued the shy unfolding of
her ideals.  "Nothing is too good for boys; no training is high enough,
because they are to be the builders and upholders of our Empire.  Don't
you think that little girls, who are destined some day to be the mates
of these boys, should be prepared in a way that will make them worthy
of their share of the inheritance?  They have to be taught ideals of
honour and courage and intelligent patriotism, so that they can help
and encourage their men in years to come.  They must learn to cook and
sew, learn the laws of Nature and hygiene, so that they can make the
home not 'an habitation enforced'—as it is for so many women—but a
place where they may with all honour bring into the world other little
girls and boys. . . ."  She drew her breath quickly.  "Ah, that is not
a thing anyone can do, teaching all that!  It must be someone who gives
all—and who gives herself gladly . . . as I have."</p>
<p id="id00199">Torps turned his head as if to speak, but checked himself.</p>
<p id="id00200">"Don't think I am setting myself upon a pedestal.  Don't think my heart
is too anaemic to—to care for you, and that I am trying to shelter
myself behind talk of a life's mission.  Oh!" she cried, "be generous.
Don't try to make it harder."</p>
<p id="id00201">She leaned towards him a little as he sat with lowered eyes.  "This is
a time of grave anxiety, isn't it?" she continued gently, as if
explaining something to an impatient child.  "You naval men ought to
know.  There is talk of war everywhere—of war with Germany.  They say
we are on the brink of it to-day."  Torps nodded.  "Supposing it came
now . . . and you were recalled.  How do they recall you?  Sound a
bugle—beat a drum?"</p>
<p id="id00202">Torps smiled faintly.  "Something of the sort—no, not a drum; a bugle,
perhaps."</p>
<p id="id00203">"Well, we'll suppose it is a drum.  One somehow associates it with war
and alarms.  Would you hesitate to obey?"  Torps refrained from the
obvious answer and plucked a grass-stem to put between his teeth.  "You
would obey, wouldn't you, because it is your duty—however much you'd
like to sit here with me?  Will you try to realise that I shall be only
answering the drum, too, when—I go back."</p>
<p id="id00204">The breeze that strayed about the floor of the Channel fanned their
faces and set the bright sea-poppies nodding all along the edge of the
cliffs.  The sun was low in the west, and a snake-like flotilla of
destroyers crept out across the quiet sea from the harbour hidden by a
fold in the hills.  Torps watched them with absent eyes, and there was
a long silence.  The wind had loosened a strand of his companion's
hair, and she was busy replacing it with deft fingers.</p>
<p id="id00205">"Margaret," he replied at last, "you said just now that I understood
very little about women.  I think you are right.  Perhaps if I
understood more I might know how to muffle the drum so that you
wouldn't hear it.  I might have learned to pipe a tune that would make
you not want to hear it. . . .  I don't know. . . .  But I accept all
you say—although deep down in my heart I know you are wrong.  There
will come a day when you, too, will know you are wrong.  I shall come
back then.  And till then, since I must"—he smiled in a whimsical, sad
way that somehow relaxed the tension—"I lend you to the children."</p>
<p id="id00206">She returned his smile quite naturally, with relief in her eyes.  "Dear
Trevor, yes . . . because they need me so. . . .  Believe me, I am not
wrong: and we keep our friendship still, sweet and sane——"  She broke
off suddenly and raised a slim forefinger, holding her head sideways to
listen, the way women and birds and children seem to hear better.
"Hark!  Did you hear?  How odd!  Listen, Trevor!"</p>
<p id="id00207">Torps brought himself back with an effort.  "Hear what?"</p>
<p id="id00208">"Listen!"</p>
<p id="id00209">He listened.</p>
<p id="id00210">"I can hear the waves along the shingle."</p>
<p id="id00211">"No, no. . . .  There—now!"</p>
<p id="id00212">"Oh! . . .  Yes, I can hear. . . .  It sounds like a drum."</p>
<p id="id00213">"Trevor, it <i>is</i> a drum, somewhere out at sea!  How odd when we were
just talking about drums—hush!  Oh, do listen. . . ."</p>
<p id="id00214">The sound, borne to them on the light wind, seemed to grow nearer; then
it waned till they could scarcely catch the beats.  Anon it swelled
louder: the unmistakable "Dub! dub! rub-a-dub! dub! . . .  Dub! dub!
dub!" of a far-off drum.</p>
<p id="id00215">Margaret shook his sleeve.  "Of course it's a drum.  It can't be
anything else, can it?"</p>
<p id="id00216">"It's Drake's Drum!" he replied, with mock solemnity.  "There's a
legend in the West Country, you know——"</p>
<p id="id00217">"I know!"  She nodded, bright eyed with interest, and rose to a
kneeling position to gaze beneath her palms out towards the west.  The
sun had set, and a thin grey haze slowly veiled the horizon.  Already
the warm afterglow was dying out of the sky.</p>
<p id="id00218">"He has 'quit the Port of Heaven,'" she quoted half-seriously, playing
with superstition as only women can, "and he's 'drumming up the
Channel'!  They say it foretells war . . . that noise. . . ."  Margaret
gave a little shiver and rose to her graceful height, extending both
her ringless hands to him.  "It's getting chilly—come!"</p>
<p id="id00219">Torps rose to his feet, too, and for a moment faced her, with his
grave, patient eyes on hers.  For the first time she noticed that his
hair was going grey about the temples, and, had he known it, Margaret
came very near to wavering in that moment.  Perhaps he did realise, and
with quick, characteristic generosity helped her.</p>
<p id="id00220">"I think I understand," he said, "something of their need—the need of
the children for such as you.  It—it——"  He turned abruptly towards
the sea.  The noise that resembled a distant drum had ceased, and there
was only the faint surge of the waves on the beaches far below.</p>
<p id="id00221">It was the only sound in all the land and sea.</p>
<p id="id00222">      *      *      *      *      *</p>
<p id="id00223">In the whitewashed coastguard station a mile away the bearded occupant
on duty was finishing his tea.  The skeleton of a herring lay on the
side of his plate, the centre of which the boatman was scouring with a
piece of bread (preparatory to occupying it with damson jam), when the
telephone bell rang.  A man of economical habits, he put the bread in
his mouth, and, rising from the table, picked up the receiver.</p>
<p id="id00224">"<i>. . . Portree Signal Station—Yes.</i>"</p>
<p id="id00225">"<i>. . . 'Oo?  Yes.</i>"</p>
<p id="id00226">He stood motionless with the receiver to his ear, his jaws moving
mechanically about the last of the piece of bread.  Outside the little
room the wind thrummed in the halliards of the signal-mast.  The clock
over the desk ticked out the deliberate seconds.  A cat, curled up by
the window, rose, stretching itself, and yawned.</p>
<p id="id00227">"<i>. . .  Prepare to mobilise.  All officers and men are recalled from
leave.  Detailed orders will follow.  Right.  Good-bye.</i>"</p>
<p id="id00228">He replaced the receiver and rang off.  Then, still masticating, he
executed a species of solemn war-dance in the middle of the floor.</p>
<p id="id00229">"Crikey!" he said aloud.  "That means war, that do!  Bloody war!"</p>
<p id="id00230">He snatched up a telescope and ran outside, still talking aloud to
himself after the manner of men who live much alone.  "I see a bloke
an' 'is young woman along there this afternoon.  I'd ha' said he was a
naval orficer if anyone was to ask me."  He scanned the hills through
his glass for a moment, and then set off along the track that skirted
the edge of the cliffs.</p>
<p id="id00231">Margaret saw him first, a broad, blue-clad figure, threading his way
among the furze bushes.  "And you won't be unhappy, will you, Trevor?"
she was saying.  "You will understand, you——"  She broke off to watch
the coastguard hurrying towards them.  "Does that sailor want to speak
to us, do you think?  He seems in a great hurry."</p>
<p id="id00232">Torps stood at her side staring.</p>
<p id="id00233">The coastguard drew near, wiping his face with a vast blue and white
spotted handkerchief, for he had been running.  "Beg pardon, sir," he
called as he came within earshot, "but would you be a naval officer?"</p>
<p id="id00234">"I am," replied Torps.  "Why?"</p>
<p id="id00235">The man saluted.  "There's a telephone message just come through, sir,<br/>

'Prepare to mobilise.  All officers and men are recalled from leave.'"<br/>
</p>
<p id="id00236">Torps stared at him.  "Where did it come from—the message?"</p>
<p id="id00237">"From the port, sir.  I was to warn anyone I saw out this way . . ."</p>
<p id="id00238">"Right; thank you.  I'm going back now."  He turned towards Margaret.<br/>

"Did you hear that?"  There was a queer note of relief in his voice.<br/>
</p>
<p id="id00239">"Yes," she replied quietly.  "The Drum."</p>
<h2 id="id00240" style="margin-top: 4em">III</h2>
<h5 id="id00241">A CAPTAIN'S FORENOON</h5>
<p id="id00242">The Captain came out of his sleeping-cabin as the last chord of the
National Anthem died away on the quarter-deck overhead with the roll of
kettledrums.</p>
<p id="id00243">"Carry on!" sang the bugle; and the ship's company, their animation
suspended while the colours crept up the jackstaff, proceeded to
"breakfast and clean."  The signalman whose duty it was to hoist the
Ensign at 8 a.m. turned up the halliards to his satisfaction, and
departed forward in the wake of the band.</p>
<p id="id00244">The Captain had "cleaned" already, and his breakfast was on the table
in his fore-cabin.  He sat down, glanced at the pile of letters beside
his plate, propped the morning paper against the teapot, and commenced
his meal.  He ate with the deliberate slowness of a man accustomed to
having meals in solitude, who has schooled himself not to abuse his
digestion.</p>
<p id="id00245">As he ate his quick eye travelled over the headlines of the paper,
occasionally concentrating on a paragraph here and there.  Ten minutes
sufficed to give him a complete grasp of the day's affairs.  The naval
appointments he read carefully.  His memory for names and individuals
was unfailing; he never forgot anyone who had served under his command,
and followed the careers of most with interest.  His daily private
correspondence, which was large, testified to the fact that not many
forgot him.</p>
<p id="id00246">Breakfast over, he laid aside the paper, lit a cigarette, and turned
over the little pile of letters, identifying the writers with a glance
at the handwriting on each envelope.  Only one was unknown to him: that
he placed last, and carried them into the after-cabin to read, leaning
his shoulder against the mantel of the tiled and brass-bound fireplace.</p>
<p id="id00247">The first letter he opened was from his wife, and, in consequence, its
contents were nobody's affair but his own.  He read it twice, and
smiled as he returned it to the envelope.</p>
<p id="id00248">The next, written on thick notepaper stamped with the Admiralty crest,
he also read twice, and mused awhile.  Apparently this also was
nobody's concern but his, for, still deep in thought, he tore it up and
put the pieces in the fire before taking up the third.  This was an
appeal for assistance from a former watch-keeper who aspired to the
Flying Corps.  The next was also a request for assistance from a young
officer, who, having recently taken a wife to his bosom, apparently
considered the achievement a qualification for the command of one of
H.M. torpedo-boat destroyers.</p>
<p id="id00249">The Captain rubbed his chin thoughtfully.  "I sent him a silver
photograph frame. . . .  He'll want me to be godfather next."  He
occasionally spoke aloud when alone.</p>
<p id="id00250">An appeal for funds for a memorial to someone or another followed.
Then two advertisements from wine merchants and a statement of his
account with his outfitter were consigned in turns into the fire.  The
last envelope, in the unknown hand, he scrutinised for a moment before
opening.  The postmark was local, the caligraphy illiterate.  He opened
the letter and read it with an inscrutable face.  Then, with a quick
movement as of disgust, he crumpled it up and threw it into the flames.
It was anonymous, and was a threat, couched in lurid and ensanguined
terms, to murder him.</p>
<p id="id00251">Judges, and post-captains of the Royal Navy, perhaps as a reminder of
their great responsibilities, occasionally receive communications of
this nature.  Their life insurance policies, however, appear to remain
much the same as those of other people.</p>
<p id="id00252">      *      *      *      *      *</p>
<p id="id00253">The after-cabin, where the Captain perused his correspondence, was an
airy, chintz-upholstered apartment leading aft through two heavy steel
doors on to the stern-walk.  The doors were open on that particular
morning, and the high, thin cries of seagulls quarrelling under the
stern drifted through almost unceasingly.</p>
<p id="id00254">Forward, the white-enamelled bulkhead was pierced by two entrances.
One led from a diminutive sleeping-cabin and bathroom, the other from
the fore-cabin, which the Captain had just quitted, and which in turn
communicated with a lobby where a marine sentry paced day and night.</p>
<p id="id00255">The after-cabin was lit by a skylight overhead and scuttles in the
ship's side.  The sunlight, streaming in through the starboard ones,
winked on the butterfly clamps of burnished brass and small rods from
which the little chintz curtains hung.  A roll-topped desk occupied a
corner near the fireplace, and round the bulkheads, affixed to white
enamelled battens, hung water-colour paintings of his ships.  A sloop
of war under full sail; a brig, close-hauled, beating out of Plymouth
Sound; a tiny gunboat at anchor in a backwater of the Upper Yangtse.
There were spick-and-span cruisers; a quaint, top-heavy looking
battleship that in her day had been considered the last word in naval
construction, and whose name to-day provokes reminiscences from the
older generation and from the younger half-dubious smiles; then, near
the door, came modern men-of-war of familiar aspect.  They represented
the milestones of a long career.</p>
<p id="id00256">A chart lay folded on a table in the centre of the cabin, with a pair
of dividers and a parallel ruler lying on it.  Another table stood in a
corner near the door—a small, glass-topped table such as collectors of
curios gather their treasures in.  The contents of this table, however,
were not curios in the strict sense of the word.  A number of them were
very commonplace objects, but each one held its particular associations.</p>
<p id="id00257">You will find just such a collection of insignificant mysteries in a
boy's pocket or a jackdaw's nest.  Bits of string, a marble polished by
friction, a piece of coloured glass, an old nail—in themselves
rubbish, but doubtless linking the possessor to some amiable memory,
and cherished for no better reason.</p>
<p id="id00258">Some men retain this instinct of boyhood.  But whereas the boy is
secretive and reticent about the particular associations his pocket
holds, the man will talk about his hoard.</p>
<p id="id00259">In the glass-topped table in that corner of the after-cabin were ties
with all the seven seas and the shores they wash.  Mementoes of folly
or friendship, sport or achievement; fragments of the mosaic that is
life.</p>
<p id="id00260">A bit of brick from the Great Wall of China recalled a bag of geese in
the clear cold dusk of Northern Asia.  Memories, too, of the whaler's
beat back to the fleet in the teeth of a rising gale that swept in from
the Pacific, when the bravest unlaced his boots and they baled with the
empty guncase.</p>
<p id="id00261">There was a piece of the sacred pavement of Mecca, brought back in the
days when few Europeans had brought anything back from there—even
their lives.  A gold medal in a morocco-leather case, won by an essay
that had called for months of unrelaxed study.  A copper bangle from
the wrist of a Korean dancing-girl (it was somebody else's story,
though).  A wooden ju-ju from Benin, dark-stained and repulsive; a tiny
clay godling that had guarded the mummied heart of an Egyptian queen.
A flint arrow-head picked up on Dartmoor during a long summer tramp
after the speckled trout.  A jewelled cigarette-case, gift of an
empress who could give no more than that, however much she may have
wanted to.</p>
<p id="id00262">Rubbish, all rubbish.  Yet occasionally, when two or three
post-captains, contemporaries and fleet-mates, gathered here to smoke
after-dinner cigars, the host would unlock the glass-topped table,
select some object from his miscellany, and hold it up with a "D'you
remember——?"  And one or other of his guests—sometimes all of
them—would laugh and nod and blow great clouds of smoke and slide into
eager reminiscence.  Yesterday is the playground of all men's hearts,
but more especially those of sailor men.  These odds and ends were only
keys that unlocked the gate.</p>
<p id="id00263">A few photographs stood on the shelf above the hearth.  Some books
occupied a revolving bookcase within reach of anyone sitting at the
desk; not very interesting books: old Navy Lists, a "King's
Regulations," a "Manual of Court Martial Procedure," one or two volumes
on International Law, and a treatise on so-called 'modern'
seamanship—which, by the way, is a misnomer, seamanship, like love,
being of all time.</p>
<p id="id00264">The revolving bookcase supported a bowl of flowers.  The Captain's
Coxswain had personally arranged them that morning; had, in fact, had a
slight difference of opinion with the Captain's valet (conducted <i>sotto
voce</i>) over the method of their arrangement.  The Coxswain won on the
claim of being a married man and understanding mysteries beyond the ken
of bachelors.  The result in either case would have brought tears to
the eyes of any woman.</p>
<p id="id00265">      *      *      *      *      *</p>
<p id="id00266">The Captain finished his cigarette and opened the roll-topped desk,
slipped his letters into a pigeon-hole, and closed the desk again.  As
he did so the Commander entered the cabin, tucking his cap under his
arm.</p>
<p id="id00267">"Nine o'clock, sir; all ready for divisions.  The Chaplain is
sick—will you read prayers?"</p>
<p id="id00268">"Sick, is he?  What's the matter?"</p>
<p id="id00269">"He twisted his knee yesterday playing football.  The Fleet Surgeon has
made him lie up."</p>
<p id="id00270">The Captain nodded.  "All right; I'll read them."  As the Commander
turned to go he spoke again: "By the way, that fellow I gave ninety
days to yesterday—was there a woman in the case, d'you happen to know?
There was nothing in the evidence, of course, but I wondered——"</p>
<p id="id00271">The Commander paused while the busy brain searched among its dockets.
The man whose business it is as Executive Officer to control the
affairs of close on a thousand of his fellow men must of necessity
sometimes learn curiously intimate details of their lives.</p>
<p id="id00272">"Yes; the Master-at-Arms mentioned to me that a woman was at the bottom
of it.  She's a wrong 'un, I understand."</p>
<p id="id00273">"Thank you."</p>
<p id="id00274">The Commander went out, and a moment later the bugle overhead blazed
forth "Divisions."</p>
<p id="id00275">"I thought it was a woman's writing," added the Captain musingly.</p>
<p id="id00276">"Divisions correct, sir!"  The Commander saluted and made his report.</p>
<p id="id00277">The Captain returned the salute briskly.  "Sound the 'Close.'"</p>
<p id="id00278">The bugle sounded again, the bell began to toll for prayers, and the
band on the after shelter-deck struck up a lively march as the men came
aft.</p>
<p id="id00279">Anyone interested in the study called physiognomy might with advantage
have taken his stand at this moment on the after part of the
quarter-deck, where the shadow of the White Ensign curved and flickered
across the planking.  Perhaps the Captain, who stood there, was himself
a student of the art.  At any rate, as the men marched aft through the
screen doors his level eyes passed from face to face, reflective,
observant, intensely alert.</p>
<p id="id00280">The last division reached its allotted position on the quarter-deck,
turned inboard, and stood easy.  The band stopped abruptly.  The bell
ceased tolling.  In the brief ensuing silence the Commander's voice was
clearly audible as he made his report.</p>
<p id="id00281">"Everybody aft, sir."</p>
<p id="id00282">The Captain slipped a small prayer-book out of a side pocket.  The
Commander gave a curt order, and five hundred heads bared to the
sunlight.</p>
<p id="id00283">"Stand easy!"</p>
<p id="id00284">There is much beauty in the sonorous periods of the English Rubric.
Read in the strong, clear voice of a man who for thirty years had known
calm and tempest, sunset and dawn at sea, the familiar words—of appeal
and praise alike—assumed somehow an unwonted significance; and when he
closed the book, slipped it back into his pocket, and looked up, the
face he raised was the face of one who, whatever else his creed had
taught him, found in all success the answer to some prayer, in every
disaster a call to courage and high endeavour.</p>
<p id="id00285">      *      *      *      *      *</p>
<p id="id00286">Down in the after-cabin, five minutes later, the Fleet Surgeon handed
the sick-list to the Captain, who read it with care.  For the first
time that day his brow clouded.  The two men looked at one another.</p>
<p id="id00287">"It is heavy," said the Fleet Surgeon; "but——"  He made an
imperceptible upward movement of the shoulders, for his mother had been
French.</p>
<p id="id00288">For some moments after he had gone the Captain stood staring out
through the after doorway.  A barge, heavily freighted, was passing
slowly down-stream.  His eyes followed the brown sail absently as long
as it was within his field of vision.  The anger had gone from his brow
and left a shadow of sadness.</p>
<p id="id00289">"'<i>Si j'etais Dieu</i>,'" he murmured, following some train of thought and
musing aloud as was his habit.  Then, still in a brown study, he opened
the roll-topped desk and pressed a bell.</p>
<p id="id00290">"Tell Mr. Gerrard I'll sign papers," he said to the marine sentry who
appeared in the doorway.</p>
<p id="id00291">"Double-O" Gerrard (so called because he wore glasses with circular
lenses and his name made you think of telephones) answered the summons,
carrying a sheaf of papers.  He was the Captain's Clerk: that is to
say, the junior accountant officer, detailed by the Captain to conduct
his official correspondence and perform secretarial work generally.
The position is not one commonly sought after, but Double-O Gerrard
appeared to enjoy his duties, and as a badge of office carried a
perpetual inkstain on the forefinger-tip of his right hand.</p>
<p id="id00292">The Captain sat down at his desk with a little sigh.  If the truth be
known, he had small relish for this business of "papers."  He picked up
his pen and examined the nib.</p>
<p id="id00293">"Do you ever use your pen to clean a pipe out?" he asked his Clerk.</p>
<p id="id00294">"Oh no, sir."</p>
<p id="id00295">"I suppose it depends on the nib one uses whether it suffers much."
With a piece of blotting paper the Captain removed fragments of tobacco
ash and nicotine from the nib, and dipped it in the ink.  "It doesn't
seem to hurt mine.  Now then, what have we got here?"</p>
<p id="id00296">A quarter of an hour later he pushed aside the last of the pile of
documents and lit a cigarette with the air of a man who had earned a
smoke.</p>
<p id="id00297">"Any defaulters?"</p>
<p id="id00298">"No, sir, none for you to-day."</p>
<p id="id00299">"Humph!  Tell the Commander I'll buy him a pair of white kid gloves
when I go ashore.  Request-men?"</p>
<p id="id00300">His Clerk placed a book upon the desk open at a list of names.  The<br/>

Captain ran down them with a pencil.<br/>
</p>
<p id="id00301">"Badges, all entitled? . . .  Stop allotment—who does he allot to?
Mother? . . .  Restoration to first class for leave. . . .  To be rated
Leading Seaman—Jones.  Jones?  Oh, yes, I know: youngster in the
quarter-deck division with a broken nose.  The Commander spoke to me
about him."  The pencil slowly descended to the bottom of the page,
ticking off each man's request as it was gone into and explained.  He
stopped at the last one.  "'To see Captain about private affairs.'
What's his trouble?"</p>
<p id="id00302">"I don't know, sir.  He put in his request to see you through the<br/>

Master-at-Arms.  He didn't say what it was about."<br/>
</p>
<p id="id00303">The Captain closed the book.  "All seamen, eh?  No Marine request-men?"</p>
<p id="id00304">"No, sir."</p>
<p id="id00305">"Right.  I'll see 'em at eleven."  The Clerk gathered the papers
together and departed.  As he went out there was a tap at the door.
The Captain frowned.  The tap was repeated.</p>
<p id="id00306">"Don't knock," he called out.  "If you've got anything to report, come
in and report it."</p>
<p id="id00307">The Chief Yeoman of Signals entered with an embarrassed air.  He was
new to the ship, and, as everyone knows, all captains have their little
peculiarities.  Here he was up against one right away.  He'd never had
much luck.</p>
<p id="id00308">"I don't want anyone to knock when they come into my cabin on duty.<br/>

I'm not a young woman in her boudoir."<br/>
</p>
<p id="id00309">"Aye, aye, sir," said the Chief Yeoman.  "Signal log, sir."</p>
<p id="id00310">      *      *      *      *      *</p>
<p id="id00311">"Don't forget now," counselled the Master-at-Arms to the request-men
fallen in on the starboard side of the quarter-deck.  "When your names
is called out, step smartly up to the table, an' keep your caps on.
You salutes when you steps up to the table an' when you leaves it."</p>
<p id="id00312">The request-men, who had heard all this a good many times before,
sucked their teeth in acquiescence.</p>
<p id="id00313">The Captain was walking up and down the other side of the deck talking<br/>

to the Commander.  They turned together and came towards the table.<br/>

The Captain's Clerk opened the request-book and laid it before the<br/>

Captain.<br/>
</p>
<p id="id00314">"'Erbert Reynolds," intoned the Master-at-Arms in a stentorian voice.<br/>

"Able seaman.  Requests award of first Good Conduct Badge."<br/>
</p>
<p id="id00315">The Captain put his finger on the first name at the top of the page,
glanced keenly at the applicant, and nodded.  "Granted."</p>
<p id="id00316">"Granted," echoed the Chief of Police, and Able Seaman Reynolds
departed with authority to wear on his left arm the triangular red
badge that vouched to his exemplary behaviour for the last three years.</p>
<p id="id00317">Five others followed in quick succession with similar requests, and
trotted forward again at a dignified and amiable gait through the
screen door.</p>
<p id="id00318">"To stop allotment."  The Captain raised his head.</p>
<p id="id00319">"Who do you allot to?"</p>
<p id="id00320">"Me mother, sir."</p>
<p id="id00321">"Doesn't she want it?"</p>
<p id="id00322">The request-man was a young stoker, little more than a boy, and his
eyes were troubled.</p>
<p id="id00323">"She don't deserve it," he replied; "she drinks, sir.  I got letters
from fr'en's——"  He thrust his hand inside the breast of his jumper
and produced his sad evidence—a letter from a clergyman, one or two
from lay-workers in some north-country slum, and one from his mother
herself, an incoherent, abusive scrawl, with liquor stains still upon
the creased paper.</p>
<p id="id00324">"I send 'er my 'arf-pay reg'lar ever since I were in the Navy, sir.
But she ain't goin' ter 'ave no more."  He made the statement without
heat or sorrow.</p>
<p id="id00325">"Stopped," said the Captain, with a nod.</p>
<p id="id00326">"Allotment stopped," repeated the Master-at-Arms, and the allotter
passed forward out of sight to whatever destiny awaited him.</p>
<p id="id00327">"To be rated Leading Seaman, sir."</p>
<p id="id00328">A tall, young Able Seaman stepped forward and fixed eyes of a clear
blue on the Captain's face.</p>
<p id="id00329">The Captain met his gaze, and for a moment threw all the weight of
thirty years' experience of men into the scales of judgment.  "There is
a vacancy for a Leading Seaman's rate in the ship," he said.  "The
Commander has recommended you for it.  You're young.  Keep it."</p>
<p id="id00330">"Rated Leading Seaman.  'Bout turn."</p>
<p id="id00331">The newly created Leading Seaman, whose nose was a reminder of the
vagaries of the main sheet block of a cutter when going about, flushed
with pleasure and turned smartly on his heel.  The vacant rate was due
to a lapse from rectitude on the part of one Biggers, leading hand of
the quarter-deck, who had returned from leave with a small flat flask
tucked inside his cholera belt.  The flask contained whisky, and had
been thrust there by a friend ashore in an access of maudlin
good-fellowship on parting.  The night had been a convivial one, and
Leading Seaman Biggers overlooked the gift until, coming on board, the
keen-eyed officer of the watch drew his attention to it.  He paid for
the misplaced generosity of his well-wisher with his "Killick."[1]</p>
<p id="id00332">He happened, moreover, to be employed in coiling down a rope—in the
capacity he had reverted to—while his supplanter received the rating;
but he eyed the ceremony stoically and without resentment.  He had
failed, and, of his less frail brethren, another was raised up in his
stead.  It was the immutable law.</p>
<p id="id00333">"To be restored to the first class for leave."</p>
<p id="id00334">A stout Able Seaman stepped forward, and, from force of a habit
engendered by long familiarity with the etiquette of the defaulters'
table, removed his cap.</p>
<p id="id00335">"<i>Put</i> yer cap on," added the Master-at-Arms in a fierce undertone.</p>
<p id="id00336">The suppliant deftly replaced his cap.  As he did so a packet of
cigarettes, a skein of darning worsted and a picture postcard
(depicting a stout lady in a pink costume surf bathing) fell out on to
the deck in the manner of an unexpected conjuring trick.  An attendant
ship's corporal retrieved them, while the conjurer affected an air of
complete detachment.</p>
<p id="id00337">The Captain glanced at the conduct book.  "Clean sheet?
Right—restored to the first class.  And see if you can't stop in it
this time."</p>
<p id="id00338">The stout one made guttural noises in his throat intended to convey
assurances of future piety, and departed with an expression that
suggested a halo had not only descended upon his head, but had been
crammed inextricably over his ears.</p>
<p id="id00339">The last request-man—the man with "private affairs"—was a small
leading stoker with a face seamed by innumerable tiny wrinkles.  His
skin resembled a piece of parchment that somebody had crumpled in a fit
of petulance and made a half-hearted attempt to smooth out again; even
his ears were crumpled.  His brown eyes, big and sad, were like the
eyes of a suffering monkey.</p>
<p id="id00340">The Commander interposed with an explanation: "This man wishes to see
you about a private matter, sir."</p>
<p id="id00341">The Captain made a little gesture with his hand, and the small group of
officers and ship's police near the table stepped back a few paces out
of earshot.  The Commander, perhaps the busiest man on board, snatched
the moment's respite to confer with the Carpenter, who had been
hovering round waiting for his opportunity.  The Master-at-Arms was
standing by the bollards alternately sucking a stump of pencil and
making cryptic notations in his request-book.  The two ship's corporals
had removed themselves with great delicacy of feeling to the screen
door, where in an undertone they settled an argument as to whose turn
it was to make out the leave tickets.  The Captain's Clerk became
interested in the progress of work in an ammunition lighter alongside.</p>
<p id="id00342">The Captain, with knitted brows, was reading a letter that had been
handed to him across the table.  He folded it up when read, and handed
it back to the recipient; then, holding his chin in his fist and
supporting the elbow with the other hand, he listened to the tale the
small man with the crumpled ears had to unfold.  It was an old
tale—old when Helen first met the eyes of Paris.  But there was no
veil of romance to soften the outline of its crude tragedy.  It was
just sordid and pitiful.</p>
<p id="id00343">For five minutes, perhaps, the two men faced each other.  At the end of
that time the Captain was leaning forward resting both hands on the
table, talking in grave, kindly tones.  He talked, not as Captains
commonly talk to Leading Stokers, but as one man might talk to another
who turned to him for advice in the bitter hour of need, drawing on the
deep well of his experience, education, and kindly judgment.</p>
<p id="id00344">"Troubles shared are troubles halved."  The Captain had said so, and
the tot of rum served out at one-bell to the little man with the
crumpled ears went some way to complete the conviction.</p>
<p id="id00345">      *      *      *      *      *</p>
<p id="id00346">Jeremiah Casey, Petty Officer and Captain's Coxswain, hauled himself
nimbly up the Jacob's ladder to the quarter-boom and came inboard.  The
Captain was walking up and down, deep in thought, with his hands linked
behind his back.  Casey pattered up and saluted.</p>
<p id="id00347">"I've bent on that noo mainsail, sir. . . .  There's a nice li'l
sailin' breeze, sir."  Casey, hinting at a spin in the galley, somehow
reminded one of a spaniel when he sees the gun-case opened.  Had he
been blessed with a tail, he would most certainly have wagged it.</p>
<p id="id00348">The Captain walked slowly aft and looked down into the galley lying at
the quarter-boom.  Few men could have resisted the appeal of that long
slim boat with the water lapping invitingly against her clinker-built
sides.  The brasswork in her gleamed in the sun like jewels set in
ivory, for the woodwork was as near the whiteness of ivory as holystone
and sharkskin could make it.  She had little white mats with blue
borders on the thwarts and in the sternsheets, and her yoke, of curious
Chinese design, had a history as mysterious and legendary as the
diamonds of Marie Antoinette.</p>
<p id="id00349">"Get her alongside," said the Captain.  "I want to try that mainsail."</p>
<p id="id00350">Five minutes later the galley was spinning across the sparkling waters
of the harbour.</p>
<p id="id00351">Once the Captain spoke, and the bowman moved his weight six inches
forward.  Then she sailed to his light touch on the helm as a violin
gives out sound under the bow of a master.</p>
<p id="id00352">Casey, sitting motionless on the bottom boards with the mainsheet in
his hands, gazed rapturously at the new mainsail, and thence into the
stolid countenance of the second stroke.</p>
<p id="id00353">"Ain't she a <i>witch</i>?" he whispered.</p>
<p id="id00354">For half an hour the galley skimmed to and fro among the anchored
fleet, now running free like a white-winged gull, anon close-hauled,
the razor bows cleaving a path through the dancing water in a little
sickle of creaming foam.</p>
<p id="id00355">The Captain brought her alongside the gangway with faultless judgment
and stood up.  Like Saul, he had taken the cares of high command to a
witch, and lo! his brow was clear and his eyes twinkled.</p>
<p id="id00356">"Yes," he said in even tones as he stepped out of the boat, "that
mainsail sets all right," and ran briskly up the ladder two steps at a
time.</p>
<p id="id00357">Casey thumbed the lacing on the yard.  "Perfection is finality, and
finality is death."</p>
<p id="id00358">"I don't know but what I wouldn't shift the strop '<i>arf</i> an inch
aft—mebbe a quarter . . ."</p>
<p id="id00359">Inboard the ship's bell struck eight times, and the boatswain's mate
began shrilly piping the hands to dinner.</p>
<p id="id00360" style="margin-top: 3em">[1] Anchor.  The distinctive badge of a leading rating.</p>
<h2 id="id00361" style="margin-top: 4em">IV</h2>
<h5 id="id00362">THE SEVEN-BELL BOAT</h5>
<p id="id00363">The last answering pendant from the Fleet shot up above the bridge
rails, and the impatient semaphore on the Flagship's bridge commenced
waving its arms.</p>
<p id="id00364">The Yeoman of the Watch in the second ship of the line steadied his
glass against an angle of the chart house.  "'Ere y'are!  Write down,
one 'and."  A Signal-boy stepped to his elbow with a pad and pencil in
readiness.</p>
<p id="id00365">"Flag—general: Leave may be granted to officers from 8.30 to 7 p.m.<br/>

Officers are not to leave the vicinity of the town, and are to be<br/>

prepared for immediate recall."  He lowered the glass sharply.<br/>

"Finish.  Down Answer!"<br/>
</p>
<p id="id00366">Obedient to the order, a Signal-man brought the long tail of bunting
down hand over hand.  He hitched the slack of the halliard to the
bridge rail and puckered his eyes, staring across the waters of the
harbour to where the roofs of houses showed among the trees.  "'Ow I
pities orficers!" he observed under his breath, and walked to the end
of the bridge.</p>
<p id="id00367">The advertisement of a cinema theatre occupied a hoarding near the
landing place; away to the left the sloping roof of what was
unmistakably a brewery bore in huge block letters the exhortation:</p>
<h5 id="id00368">    DRINK PALE ALE</h5>
<p id="id00369">"Not 'arf," murmured the cynic at the end of the battleship's bridge.<br/>

He mused darkly and added, "I don't think."<br/>
</p>
<p id="id00370">The Yeoman of the Watch took the pad from the boy's hand, scribbled a
notation on it, and handed it back: "Commander an' Officer of the
Watch, Wardroom, Gunroom, an' Warrant Officers' Mess.  Smart!"</p>
<p id="id00371">The boy flung himself down the ladder, sped aft along the fore-and-aft
bridge, turned at the shelter-deck, descended another ladder, and
brought up in the battery.  Here the Commander came in view, conferring
mysteriously with the Boatswain over a length of six-inch wire hawser
that lay along the upper deck.  The Boatswain, with gloom in his
countenance, was indicating a section where the strands were flattened
and the hemp "heart" protruded in a manner indicating that all was not
well with the six-inch wire hawser.  In fact, it rather resembled a
snake that had been run over.  The Commander was rubbing his chin
thoughtfully.</p>
<p id="id00372">The Signal-boy hovered on the outskirts of the conference.  Bitter
experience in the past had taught him not to obtrude when deep called
thus to deep.</p>
<p id="id00373">"We must cut it where it's nipped, and put a splice in it, Mr.<br/>

Cassidy," the Commander was saying, and turned his head.<br/>
</p>
<p id="id00374">The boy seized the opportunity to thrust the pad within range of the
Commander's vision, one eye cocked on his face to note the effect of
this momentous communication.  He half expected that the Commander
would throw his cap in the air and shout "Hurrah!"</p>
<p id="id00375">The Commander read it unmoved.  "Show it to the Officer of the Watch,"
he said, and turned again to the wire hawser.  Truly a man of iron,
reflected the Signal-boy as he saluted and ran aft in search of the
Officer of the Watch.</p>
<p id="id00376">The Officer of the Watch received the intelligence with almost equal
unconcern, but when the boy had departed out of earshot he said
something in an undertone and added: "Just my blooming luck."  Then,
raising his voice, he shouted: "Quartermaster!  Picket-boat alongside
at three-thirty for officers."</p>
<p id="id00377">A head emerged from the hood of the after turret.  The Gunnery
Lieutenant, wearing over-alls, a streak of dirt running diagonally down
one cheek, emerged and drew off a greasy glove to wipe his face.</p>
<p id="id00378">"Did I hear you say anything about a seven-bell boat?"</p>
<p id="id00379">The Officer of the Watch nodded.  "There's leave from three-thirty to
seven p.m.  It's three o'clock now, so I advise you to smack it about
and clean if you're going ashore."</p>
<p id="id00380">The Gunnery Lieutenant slid gracefully down the sloping shield of the
turret.  Fortunately, the consideration of paint-work vanished with the
red dawn of August 5th, 1914.</p>
<p id="id00381">"My word!" he said, staring towards the distant town.  "My missus——"
and vanished down the hatchway.</p>
<p id="id00382">In the meanwhile the Signal-boy had descended to the wardroom, where he
swiftly pinned the signal on to the notice board.  The occupants of the
arm-chairs and settee followed his movements with drowsy interest.</p>
<p id="id00383">The Young Doctor rose and walked to the notice board.</p>
<p id="id00384">"Snooks!" he ejaculated.  "Leave!"  And, with a glance at the clock,
hurried out of the mess.</p>
<p id="id00385">The remainder of his messmates sat up with excitement.</p>
<p id="id00386">"What time?"</p>
<p id="id00387">"When till?"</p>
<p id="id00388">"What about a boat?"</p>
<p id="id00389">The head of the Officer of the Watch appeared through the open skylight
overhead.  "Wake up, you Weary Willies.  There's a boat to the beach at
seven-bells."</p>
<p id="id00390">"Come along, chaps," snorted the Major of Marines.  "<i>Allons nous
shifter</i>—let us shift."  And he, too, made tracks for his cabin,
followed by everybody who could be spared by "the exigencies of the
service" to experience for three blessed hours the joys of the land.</p>
<p id="id00391">The shrill voices of the Midshipmen at their toilet in the after flat
proclaimed that the precious moments were flying.  Three were
simultaneously performing their ablutions in one basin, the supply of
water to the bathroom having failed with a suddenness that could only
be attributed to the malignant agency of the Captain of the Hold.</p>
<p id="id00392">Another burrowed feverishly in the depths of his sea-chest, presenting
to the flat much the same appearance as a terrier does when busy at a
rabbit-hole.  He emerged flushed but triumphant with a limp garment in
his grasp.  "I knew I had a clean shirt," he confided to his neighbour.
"I told my servant so a fortnight ago.  He swore that every one I
possessed had been left behind in the wash at Malta."</p>
<p id="id00393">His neighbour made no reply, being in the throes of buttoning a collar
which fitted him admirably at Osborne College, but which somehow had
lately exhibited an obstinate determination to meet no more round his
neck.  However, physical strength achieved the miracle, and he breathed
deeply.  "I shouldn't sweat to shift your shirt," he consoled.  "It
looks all right.  Turn the cuffs up."</p>
<p id="id00394">"I've turned them up three times already," replied the excavator,
donning his find.  "There are limits."</p>
<p id="id00395">Another Midshipman came across the crowded flat and calmly rummaged in
the open till of the speaker's sea-chest.  "Where's your hair juice?
All right, I've got it."  He anointed himself generously with a
mysterious green fluid out of a bottle.  "My people are staying at a
pub ashore here.  Will you come and have tea, Jaggers?  Kedgeree's
coming, too."</p>
<p id="id00396">The owner of the green unguent, who was feverishly dusting his boots
with a pyjama jacket, signified his pleasure in accepting the
invitation.</p>
<p id="id00397">The sentry on the aft-deck stepped to the head of the ladder with a
bellows, on the mouth of which a small fog-horn was fitted, and gave a
loud blast.  It was the customary warning that the officers' boat would
be alongside in five minutes.</p>
<p id="id00398">The Assistant Clerk ran distractedly for the ladder.</p>
<p id="id00399">"There's one 'G'!  Have I got time to borrow five bob from the messman
before the boat shoves off?"</p>
<p id="id00400">"You might borrow five bob for me while you're about it," shouted a
belated Engineroom Watchkeeper, struggling into his clothes.</p>
<p id="id00401">"And me, too," called another.  "Buck up, for the Lord's sake, and
we'll have poached eggs for tea."</p>
<p id="id00402">"And cherry jam," supplemented another visionary voluptuously, "and
radishes."</p>
<p id="id00403">Here a figure, who had been sitting on the lid of his chest swinging
his legs, tilted his cap on to the back of his head with a snort that
suggested outlawry and defiance to the world at large.</p>
<p id="id00404">"Hallo!" exclaimed a neighbour, wielding a clothes-brush with energy.<br/>

"What's up?  Aren't you coming ashore?  It isn't your First Dog, is it?"<br/>
</p>
<p id="id00405">The outlaw shook his head.  "No; my leave's jambed.  You know that
beastly six-inch wire hawser?  We were bringing it to the after capstan
yesterday, and the Commander——"</p>
<p id="id00406">The aft-deck sentry gave two blasts on his fog-horn.  Chest lids
banged, keys rattled.</p>
<p id="id00407">"Jolly rough luck!" commiserated his friend, and joined the stampede
for the quarterdeck.</p>
<p id="id00408">In thirty seconds the flat was deserted save for the disconsolate
figure swinging his legs.  Presently he climbed down from his chest and
wended his way by devious and stealthy routes to the after
conning-tower, where he smoked a surreptitious cigarette in defiance of
the King's Regulations and Admiralty Instructions (his age being
sixteen) and felt better.</p>
<p id="id00409">In the meanwhile the picket-boat was driving her way shoreward with the
emancipated members of Wardroom and Gunroom clustered on top of the
cabin and in the stern sheets.</p>
<p id="id00410">"Bunje," said the First Lieutenant, "come to the club and have tea and
play 'pills' afterwards?"</p>
<p id="id00411">The Indiarubber Man shook his head.  "No, thanks; I'm afraid I—I've
got something else to do."</p>
<p id="id00412">The Paymaster contemplated him thoughtfully.  "Bunje, my lad, the
darkest suspicions fill my breast.  Wherefore these carefully creased
trousers, this liberal display of fine linen and flashing cuff-links
withal?  Our Sunday monkey-jacket, too.  Can it be——?  No."  He
appealed to the occupants of the stern sheets: "Don't tell me the lad
is going poodle-faking!"[1]</p>
<p id="id00413">"His hands are warm and moist," confirmed one of the Watchkeepers.  "He
wipes them furtively on the slack of his trousers in frightened
anticipation."</p>
<p id="id00414">The Indiarubber Man reddened.  "You silly asses!"</p>
<p id="id00415">The Junior Watchkeeper squirmed with delight.  "He is—he <i>is</i>! He's
going poodle-faking.  And in war time, too!  You dog, Bunje!"</p>
<p id="id00416">"Can't a fellow know people ashore without a lot of untutored clowns
trying to be funny about it?" demanded the victim.</p>
<p id="id00417">"It's the spring," said the Young Doctor.  "Bunje's young fancy is
lightly turning—yes, it is."  The Surgeon sniffed the air judicially.
"The bay rum upon your hair proclaims it.  Ah, me!  The heyday of
youth!"  He sighed.  "'Time was when love and I were well acquainted.'"</p>
<p id="id00418">"That's a fact," retorted the Indiarubber Man bitterly.  "But you
needn't brag about it.  I haven't been shipmates with you for four
years for nothing.  There's nothing you can tell me about your hideous
past that I don't know already."</p>
<p id="id00419">The picket-boat slid alongside the landing place and went astern.</p>
<p id="id00420">The Engineer Commander made his way towards the little cabin.  As the
senior officer of the party, his was the privilege of embarking last
and disembarking first.  "Don't wait for me," he said.  "Unstow!  I've
got to get my golf-clubs."</p>
<p id="id00421">The Indiarubber Man took him at his word.  "Right.  I'll carry on, if I
may."  He leaped ashore, and set off with long strides in the direction
of the town.</p>
<p id="id00422">The First Lieutenant gazed after him.  There was a general feeling that
the Indiarubber Man had suddenly assumed an unfamiliar and inexplicable
role.  "Now, what the devil is he up to, I wonder?"</p>
<p id="id00423">The others, mystified, shook their heads.</p>
<p id="id00424" style="margin-top: 3em">2</p>
<p id="id00425">The mothers of Midshipmen have a means of scenting the whereabouts of a
fleet that mere censorship of letters cannot balk.  There were at least
half a dozen mothers in the <i>foyer</i> of the big, garish hotel on the
sea-front.  Some were tête-à-tête with their sons in snug, upholstered
corners, learning aspects of naval warfare that no historian will ever
record.  Others presided over heavily laden tea-tables at which their
sons and their sons' more intimate friends were dealing with eggs and
buttered toast, marmalade, watercress, plum-cake, and toasted scones in
a manner which convinced their half-alarmed relatives that famine must
have stalked the British Navy ever since the War started.</p>
<p id="id00426">"We shall never have time," said one mother, "to hear all you have to
tell, dear."</p>
<p id="id00427">"There's really nothing very much to tell you about, mother.  Can I
order some more jam?  And Jaggers could scoff some more eggs, couldn't
you, Jag?  Waiter, two more poached eggs and some more strawberry jam.
You see, dear, we haven't done anything exciting yet.  That's all been
the luck of the battle-cruisers and destroyers.  They've had a topping
rag—three of our term have been wounded already.  But we aren't
allowed to gas about what we're going to do—why, that waiter might be
a German spy, for all we know."</p>
<p id="id00428">"Didn't know the Admiral confided his plans for the future to<br/>

Midshipmen," commented an amused father, who had run down from the War<br/>

Office for the day.<br/>
</p>
<p id="id00429">"He doesn't <i>confide</i> them," admitted another, "but my chest is in the
flat outside his steward's cabin, and, of course, <i>he</i> hears an awful
lot."</p>
<p id="id00430">"But, Georgie, tell us about your life.  Do you get enough sleep?"
queried his mother.</p>
<p id="id00431">"Rather," replied her son, whose horizon three months before had been
bounded by the playing fields of Dartmouth College, where the
dormitories are maintained at an even temperature by costly and
hygienic methods.  "We're in four watches, you know—we get one night
in in four.  At sea we sleep at our guns.  I've got one of the
six-inch, and we get up quite a good fug in our casemate at night.
Jaggers dosses in the after-control.  It's a bit breezy up there, isn't
it, Old Bird?"</p>
<p id="id00432">The Old Bird signified that the rigours of Arctic exploration were as
nothing to what he had undergone.</p>
<p id="id00433">"And your swimming-jacket—the one Aunt Jessie sent you?  The outfitter
said it was quite comfortable to wear.  I hope you always do wear it at
sea, in case—in case you should ever need it."</p>
<p id="id00434">Her son chuckled.  "The pneumatic one?  Well, we liked it awfully when
it came, and we blew it up; and then we thought we'd have a bit of
scrum practice one night after dinner, and we rolled it up for a ball,
and—and the half wasn't nippy enough in getting it away to the
three-quarters, and somehow or another it got punctured.  But I wear it
all right, mother.  It's jolly warm at nights."</p>
<p id="id00435">"And do you like your officers—is the Captain kind to you all?"</p>
<p id="id00436">The boy stirred his tea thoughtfully.</p>
<p id="id00437">"They're a topping lot.  One has got the Humane Society's gold medal
for jumping on top of a shark at Perim when it was just going to collar
a fellow bathing—you'd never think it to look at him.  There's another
we call the Indiarubber Man, who takes us at physical drill every
morning.  He's frightfully strong, and they say he licked the Japanese
ju-jitsu man they had at the School of Physical Training.  And, of
course, there's old Beggs.  You know, he was captain of
England—Rugger—some years ago.  He's broken his nose three
times. . . ."</p>
<p id="id00438">"We all skylark together in the dog-watches," added another.  "We put a
seining-net round the quarter-deck, and play cricket or deck hockey
every evening after tea to keep fit."</p>
<p id="id00439">"And they come into the gun-room when we have a sing-song on guest
nights, and kick up a frightful shine.  Oh, they're an awful fine lot."</p>
<p id="id00440">"The Captain is a topper, too.  He has us to breakfast in turns."</p>
<p id="id00441">A third took up the epic.  If you have ever heard schoolboys vie with
each other to laud and honour the glory of their own particular House
among strangers in a strange land, you can imagine much that cannot be
conveyed with the pen.  There were similar tea parties in various
corners of the hotel and in lodgings along the sea-front, but the
conversation at all of them ran on much the same lines, and this may be
considered a fair sample of the majority.</p>
<p id="id00442">"He gives a lecture every few days showing what is going on at the
front.  His brother's a General, and, of course, he gets any amount of
tips from him.  The brother of one of our Snotties—Karrard—was killed
at Mons, and the Captain sent for Karrard (who's rather a kid and felt
it awfully) and showed him a letter from the General about Karrard's
brother—he had seen him killed—which bucked Karrard up tremendously.
In fact, he rather puts on side now, because he's the only one in the
gun-room who has lost a brother."</p>
<p id="id00443">"And you don't wish you were back at Dartmouth again?"</p>
<p id="id00444">"Dartmouth!"  The speaker's voice was almost scornful.  "Why, mother.
Kedgeree here would have got his First Eleven cap this term if we'd
stayed, and even he——"</p>
<p id="id00445">A small midshipman with remarkable steel-grey eyes, who had not
hitherto spoken much, shook his head emphatically and flushed at
hearing his nickname pronounced in open conversation ashore.  "We were
treated like kids there," he explained.  "But now——"  He jerked his
head towards the north with that unfailing sense of the cardinal points
of the compass which a seaman acquires in earliest youth, or not at
all.  Somewhere in that direction the German fleet was presumed to be
skulking.  "It's different," he ended a little lamely.</p>
<p id="id00446">Suddenly the son leaned forward and pressed his mother's knee under the
table.  A tall, sinewy Engineer Commander was walking across the
<i>foyer</i> on his way to the billiard room.</p>
<p id="id00447">"There, mother, that's old Beggs.  He had our term at Osborne.  Did you
see his nose? . . .  Captain of England!" . . .  The speaker broke off
and lifted his head, listening.</p>
<p id="id00448">Through the doorway opening on to the sea-front there drifted a faint
sound, the silvery note of a distant bugle.</p>
<p id="id00449">"Hush!" said one of the others, raising a warning hand.  "Listen!"</p>
<p id="id00450" style="margin-top: 3em">3</p>
<p id="id00451">At the window of one of the detached houses in the residential part of
the town a small Naval Cadet stood with his nose flattened against the
window-pane.</p>
<p id="id00452">"I say, Betty," he ejaculated presently, "they're giving leave to the<br/>

Fleet.  I can see crowds of officers coming ashore."<br/>
</p>
<p id="id00453">His sister continued to knit industriously.  "Well, I don't suppose any
of them are coming here.  You needn't get so excited."</p>
<p id="id00454">Her brother watched the uniformed figures filing along the distant road
from the landing place.  "I hope this war goes on for another couple of
years," he sighed.</p>
<p id="id00455">"Joe!  You mustn't say such dreadful things.  You don't know what
you're talking about."</p>
<p id="id00456">"That's all jolly fine, but you haven't got to do another year at
Osborne——  I say, Betty, one of them <i>is</i> coming here!  How jolly
exciting!  He's coming up the avenue now.  He's got red hair. . . .  I
believe—yes, it's—what was the name of that Lieutenant at Jack's
wedding, d'you remember?  The funny man.  He made you giggle all the
time."</p>
<p id="id00457">For a moment the knitting appeared to demand his sister's undivided
attention; she bent her head over it.  "That was a long time
ago—before I put my hair up.  I'm sure I didn't giggle either.  Oh,
yes, I think I remember who you mean.  Is he coming here?  I
wonder—come away from the window, Joe!"</p>
<p id="id00458">The front door bell rang in a distant part of the house; she dropped
her knitting on a small side table and walked quietly out of the room.
"I'll tell mother," she said as she went out.</p>
<p id="id00459">"You needn't trouble to do that," said Joe.  "She's out—I thought you
knew."  But the door had closed.</p>
<p id="id00460">A moment later the Indiarubber Man was ushered in.  The two
representatives of His Majesty's Navy shook hands.  "I recognised you
from your photograph," said the host.  "D'you remember the wedding
group?  You were a groomsman when Jack and Milly were married, weren't
you?"</p>
<p id="id00461">"I was," replied the Indiarubber Man.  "I performed a number of menial
offices that day.  But were you there?  I don't seem to remember you."</p>
<p id="id00462">Joe shook his head.  "No, I had mumps.  Wasn't it rot?  It must have
been an awful good rag.  But I remember about you because Betty told me
afterwards—she's my sister, you know.  She said you were—oh, here she
is."</p>
<p id="id00463">Betty entered.  She cast one swift glance at her brother that might
have been intended to convey interrogation or admonition, or both, and
then greeted the Indiarubber Man with friendly composure.  "How nice of
you to come and see us!  Mother is out, I'm afraid, but she will
probably be in presently.  Do sit down.  Yes, of course I remember
you—Joe, ring the bell, and we'll have tea."</p>
<p id="id00464">"We were 'opposite numbers' at your brother's wedding," said the
Indiarubber Man, taking a seat, and nervously hitching up the legs of
his trousers to an unnecessary extent.</p>
<p id="id00465">"Yes, I remember restraining you with difficulty from going into the
garden to eat worms!  Nobody——" she broke off abruptly.  "What a long
time ago that seems!"  She laughed quietly and considered him with
merriment in her pretty eyes.  The Indiarubber Man made a swift mental
comparison between the schoolgirl bridesmaid who vied with midshipmen
in devouring ices, and his hostess of three years or so later.</p>
<p id="id00466">"Doesn't it?" he said.  For one instant their eyes met, shyly
questioning, a little curious.  The laughter died out of hers.</p>
<p id="id00467">"My eldest brother's in the North Sea now.  We haven't seen him since
the War started."</p>
<p id="id00468">The Indiarubber Man nodded.  "Yes, he's in a battle-cruiser, isn't he?
We don't get ashore much either, as a matter of fact.  But to-day——"
He entered into a lengthy statement of naval policy that led up to his
visit and the circumstances connected with it.  It was a rather tedious
explanation, but it filled in the time till tea arrived, when Betty
busied herself among the tea-cups; her brother drew his chair close to
their guest, and sat regarding him with breathless expectancy.  Was
this the side-splitting humorist Betty had talked so much about for
months after the wedding—and then abruptly refused to mention again?</p>
<p id="id00469">Joe experienced a growing sense of disillusionment.  There was nothing
about the Indiarubber Man's conversation to justify high hopes of
laughter-provoking humour.  In fact, the guest's general demeanour
compared unfavourably with that of the curate—a shy young man, victim
(had Joe but known it) of a hopeless and unrequited passion.</p>
<p id="id00470">Joe handed the Indiarubber Man his cup with the air of one prepared to
enjoy at all events the spectacle of a juggling trick with the teaspoon
or saucer.  The guest's chief concern, however, appeared to be in
finding a more secure resting-place for it than his knee, coupled with
anxiety not to drop crumbs on the carpet.</p>
<p id="id00471">Betty, presiding behind the silver tea-tray, had adopted her most
grown-up manner.  Decidedly it was all Betty's fault, therefore.  The
most confirmed humorist could hardly be expected to indulge in
drolleries in the presence of a girl who stuck her nose in the air and
put on enough side for six.  It became increasingly obvious that the
depressed jester must straightway be removed from this blighting
influence or ever the cap and bells would jingle.</p>
<p id="id00472">No sooner was tea over, therefore, than Joe sprang to his feet.  "I
say, would you like to go for a walk?"  Once outside, the flower of wit
would expand without a doubt.</p>
<p id="id00473">The Indiarubber Man appeared nonplussed at the proposal.  "I—it's very
kind of you——"  Then he turned to Betty.  "Shall we all three go for
a walk?"</p>
<p id="id00474">"Oh, it's no use asking her to go for a proper walk," interposed the
alarmed Joe.  "Her skirts are too narrow; she can't keep step, or jump
ditches, or anything."</p>
<p id="id00475">Betty laughed.  "Are you anxious to jump ditches, Mr. Standish?
Because, if not, I think I might be able to keep up with you both."
She rose to her feet, a slim, gracefully modelled young woman who
looked perfectly capable of keeping up with anyone—or of jumping
ditches, too, for that matter.  "I'll get my things if you will wait a
second."  Joe, unseen by their guest, made a face at her of unfeigned
brotherly disgust.</p>
<p id="id00476">In the open air, however, the guest's spirits gave no more evidence of
an upward tendency than they had indoors.  The trio walked, via the sea
front, to the gardens on top of the cliffs that overlooked the harbour.
Joe directed the conversation; it was largely concerned with battle and
bloodshed.</p>
<p id="id00477">"Mr. Standish, what do you do in action?" he asked presently.</p>
<p id="id00478">"Nothing," was the reply.  "I just put my fingers in my ears and shut
my eyes—I'm the officer of the after turret.  But when it's all over I
put on overalls and crawl about the works on my stomach and get a dirty
face with the best of them.  A wit once defined a turret as a bundle of
tricks done up in armour."</p>
<p id="id00479">"Is it thick armour?" asked Betty.</p>
<p id="id00480">"They tell me it is—fellows on board who pretend to know everything.<br/>

But I suspect that to be a mere ruse to get me to stay inside it."<br/>
</p>
<p id="id00481">Joe sighed.  "I <i>do</i> envy you," he said.  "Everyone seems to have
something to do, 'cept me.  Even Betty here——"</p>
<p id="id00482">The Indiarubber Man turned his head sharply.  "Why, what——"</p>
<p id="id00483">Betty turned pink.  "I'm going to nurse—on the East Coast.  My old
school has been turned into a hospital.  And the other day Miss
Dacre—she was the principal, you know, and she is nursing there
now—wrote to mother and said they would take me."</p>
<p id="id00484">"But," said the Indiarubber Man, "d'you think you could stick
it—hacking off fellows' legs, and that sort of thing?  Blessed if I
could do it."</p>
<p id="id00485">"Oh, yes," was the calm reply.  "I passed all my exams, a long time
ago—in fact, I've been working down here at this hospital for the last
six months.  We learned a good deal at school, you see.  Home nursing,
and so on."</p>
<p id="id00486">"Did you, by Jove!  Simple dishes for the sick-room and spica bandages,
and all the rest of it?"</p>
<p id="id00487">Betty laughed.  "Oh, yes, all that."</p>
<p id="id00488">The Indiarubber Man glanced at her small, capable hands, and from them
to the dainty profile beside him.  "Well," he said, "if I get bent by
an eight-inch shell I shall know where to come."</p>
<p id="id00489">Betty laughed again; "I should have to look that up in a book, then,
before I nursed you.  It might mean complications!"</p>
<p id="id00490">"It might," replied the Indiarubber Man.</p>
<p id="id00491">From the town below, where here and there a window went suddenly aflare
with the reflection of the sunset-light, there drifted up to them the
faint, clear call of a bugle.  Another took it up along the front, and
yet another.  The Indiarubber Man raised his head abruptly.</p>
<p id="id00492">"That's the recall!" he said, and turned towards the ships.  "Yes,
they've hoisted the Blue Peter.  I wonder—the boats are coming in,
too."</p>
<p id="id00493">"Does that mean you must go at once?"</p>
<p id="id00494">He nodded soberly.  "I'm afraid so," and held out his hand.  "Good-bye."</p>
<p id="id00495">"Hallo!" said Joe.  "I say, you're not off, are you?  What's up?"</p>
<p id="id00496">"That's what I'm going to find out," was the reply.  "I believe it's
another of their dodges to lure me inside my turret.  Good-bye, Miss
Betty.  Don't forget to read up the book of the words—in case of
complications. . . .  Good-bye!"  The Indiarubber Man departed down one
of the steep paths that led to the lower road and the landing-place.
The brother and sister turned and walked slowly back to the house.</p>
<p id="id00497">Their conversation on the way was confined to speculation on the part
of Joe as to the reason for this sudden recall.  His theories covered a
wide range of possibilities.  Only when they reached the house did
Betty volunteer a remark, and then in the privacy of her own room,
whose window looked out across the harbour and the sea.</p>
<p id="id00498">"Oh, I hate the War," she said.  "I hate it, I <i>hate</i> it. . . ."</p>
<p id="id00499" style="margin-top: 3em">[1] Paying calls.</p>
<h2 id="id00500" style="margin-top: 4em">V</h2>
<h5 id="id00501">THE KING'S PARDON</h5>
<p id="id00502">Ask the first thousand bluejackets you meet ashore, any afternoon the
Fleet is giving leave, why they joined the navy.  Nine hundred and
ninety-nine will eye you suspiciously, awaiting the inevitable tract.  If
none is forthcoming they will give a short, grim laugh, shake their
heads, and, as likely as not, expectorate.  These portents may be taken
to imply that they really do not know themselves, or are too shy to say
so, if they do.</p>
<p id="id00503">The thousandth does not laugh.  He may shake his head; spit he certainly
will.  And then, scenting silent sympathy, he guides you to a quiet
bar-parlour where you can pay for his beer while he talks.</p>
<p id="id00504">This is the man with a past and a grievance.</p>
<p id="id00505">      *      *      *      *      *</p>
<p id="id00506">Nosey Baines, Stoker Second-class, was a man with a past.  He also owned
a grievance when he presented himself for entry into His Majesty's Navy.
They were about his only possessions.</p>
<p id="id00507">"Nosey" was not, of course, his strict baptismal name.  That was
Orson—no less.  Therein lay the past.  "Nosey" was the result of facial
peculiarities quite beyond his control.  His nose was out of proportion
to the remainder of his features.  This system of nomenclature survives
from the Stone Age, and, sailors being conservative folk, still finds
favour on the lower-decks of H.M. Ships and Vessels.</p>
<p id="id00508">The Writer in the Certificate Office at the Naval Depot, where Nosey
Baines was entered for service as a Second-class Stoker under training,
had had a busy morning.  There had been a rush of new entries owing to
the conclusion of the hop-picking season, the insolvency of a local
ginger-beer bottling factory, and other mysterious influences.  Nosey's
parchment certificate (that document which accompanies a man from ship to
ship, and, containing all particulars relating to him, is said to be a
man's passport through life) was the nineteenth he had made out that
morning.</p>
<p id="id00509">"Name?"</p>
<p id="id00510">Nosey spelt it patiently.</p>
<p id="id00511">"Religion?"</p>
<p id="id00512">Nosey looked sheepish and rather flattered—as a Hottentot might if you
asked him for the address of his tailor.  The Writer gave the surface of
the parchment a preparatory rub with a piece of indiarubber.  "Well, come
on—R. C., Church of England, Methodist . . . ?"</p>
<p id="id00513">Nosey selected the second alternative.  It sounded patriotic at all
events.</p>
<p id="id00514">"Next o' kin?  Nearest relative?"</p>
<p id="id00515">"Never 'ad none," replied Nosey haughtily.  "I'm a norfun."</p>
<p id="id00516">"Ain't you got <i>no</i> one?" asked the weary Writer.  He had been doing this
sort of thing for the last eighteen months, and it rather bored him.
"S'pose you was to die—wouldn't you like no one to be told?"</p>
<p id="id00517">Nosey brought his black brows together with a scowl and shook his head.
This was what he wanted, an opportunity to declare his antagonism to all
the gentler influences of the land. . . .  If he were to die, even . . .</p>
<p id="id00518">The Ship's Corporal, waiting to guide him to the New Entry Mess, touched
him on the elbow.  The Writer was gathering his papers together.  A
sudden wave of forlornness swept over Nosey.  He wanted his dinner, and
was filled with emptiness and self-pity.  The world was vast and
disinterested in him.  There were evidences on all sides of an unfamiliar
and terrifying discipline. . . .</p>
<p id="id00519">"You come allonger me," said the voice of the Ship's Corporal, a deep,
alarming voice, calculated to inspire awe and reverence in the breast of
a new entry.  Nosey turned, and then stopped irresolutely.  If he were to
die——</p>
<p id="id00520">"'Ere," he said, relenting.  "Nex' o' kin—I ain't got none.  But I
gotter fren'."  He coloured hotly.  "Miss Abel's 'er name; 14 Golder's
Square, Bloomsbury, London.  Miss J. Abel."</p>
<p id="id00521">This was Janie—the Grievance.  It was to punish Janie that Nosey had
flung in his lot with those who go down to the sea in ships.</p>
<p id="id00522">Prior to this drastic step Nosey had been an errand-boy, a rather
superior kind of errandboy, who went his rounds on a ramshackle bicycle
with a carrier fixed in front.  Painted in large letters on the carrier
was the legend:</p>
<p id="id00523">    J. HOLMES &amp; SON,<br/>

    FISHMONGER ICE, ETC.,<br/>
</p>
<p id="id00524">and below, in much smaller letters, "Cash on delivery."</p>
<p id="id00525">Janie was a general servant in a Bloomsbury boarding-house.  She it was
who answered the area door when Nosey called to deliver such kippers and
smoked haddock as were destined by the gods and Mr. Holmes for the
boarding-house breakfast table.</p>
<p id="id00526">It is hard to say in what respect Janie lit the flame of love within
Nosey's breast.  She was diminutive and flat-chested; her skin was sallow
from life-long confinement in basement sculleries and the atmosphere of
the Bloomsbury boarding-house.  She had little beady black eyes, and a
print dress that didn't fit her at all well.  One stocking was generally
coming down in folds over her ankle.  Her hands were chapped and
nubbly—pathetic as the toil-worn hands of a woman alone can be.
Altogether she was just the little unlovely slavey of fiction and the
drama and everyday life in boarding-house-land.</p>
<p id="id00527">Yet the fishmonger's errand-boy—Orson Baines, by your leave, and captain
of his soul—loved her as not even Antony loved Cleopatra.</p>
<p id="id00528">Janie met him every other Sunday as near three o'clock as she could get
away.  The Sunday boarding-house luncheon included soup on its menu,
which meant more plates to wash up than usual.  They met under the third
lamp-post on the left-hand side going towards the British Museum.</p>
<p id="id00529">Once a fortnight, from 8 p.m. till 10 p.m., Janie tasted the penultimate
triumph of womanhood.  She was courted.  Poor Janie!</p>
<p id="id00530">No daughter of Eve had less of the coquette in her composition.  Not for
a moment did she realise the furrows that she was ploughing in Nosey's
amiable soul.  Other girls walked out on their Sundays.  The possession
of a young man—even a fishmonger's errand-boy on twelve bob a week—was
a necessary adjunct to life itself.  Of all that "walking out" implied:
of love, even as it was understood in Bloomsbury basements, Janie's
anaemic little heart suspected very little; but romance was there,
fluttering tattered ribbons, luring her on through the drab fog of her
workaday existence.</p>
<p id="id00531">It was otherwise with Nosey.  His love for Janie was a very real affair,
although what sowed the seeds was not apparent, and although the soil in
which they took root and thrived—the daily interviews at the area door
and these fortnightly strolls—seemed, on the face of it, inadequate.
Perhaps he owed his queer gift of constancy to the mysterious past that
gave him his baptismal name.  They were both unusual.</p>
<p id="id00532">A certain Sunday afternoon in early autumn found them sitting side by
side on a seat in a grubby London square.  Janie, gripping the handle of
cook's borrowed umbrella, which she held perpendicularly before her, the
toes of her large boots turned a little inwards, was sucking a peppermint
bull's-eye.</p>
<p id="id00533">To Nosey the hour and the place seemed propitious, and he proposed heroic
marriage.</p>
<p id="id00534">"Lor!" gasped Janie, staring before her at the autumn tints that were
powdering the dingy elms with gold-dust.  There was mingled pride and
perplexity in her tones; slowly she savoured the romantic moment to the
full, turning it over in her mind as the bull's-eye revolved in her
cheek, before finally putting it from her.  Then:</p>
<p id="id00535">"I couldn't marry you," she said gently.  "You ain't got no prospecks."
Walking out with twelve bob a week was one thing; marriage quite a
different matter.</p>
<p id="id00536">In the Orphanage where she had been reared from infancy the far-seeing
Sisters had, perhaps, not been unmindful of the possibility of this
moment.  A single life of drudgery and hardship, even as a boarding-house
slavey, meant, if nothing more, meals and a roof over her head.
Improvident marriage demanded, sooner or later, starvation.  This one
star remained to guide her when all else of the good Sisters' teaching
grew dim in her memory.</p>
<p id="id00537">Prospecks—marry without and you were done.  So ran Janie's philosophy.<br/>

The remains of the bull's-eye faded into dissolution.<br/>
</p>
<p id="id00538">Nosey was aghast.  The perfidy of women!  "You led me on!" he cried.
"You bin carry in' on wiv me. . . .  'Ow could you?  Pictur' palaces an'
fried fish suppers an' all."  He referred to the sweets of their
courtship.  "'Ow, Janie!"</p>
<p id="id00539">Janie wept.</p>
<p id="id00540">After that the daily meetings at the area door were not to be thought of.
Nosey flung himself off in a rage, and for two successive nights
contemplated suicide from the parapet of Westminster Bridge.  The irksome
round of duties on the ramshackle bicycle became impossible.  The very
traffic murmured the name of Janie in his ears.  London stifled him; he
wanted to get away and bury himself and his grief in new surroundings.
Then his eye was caught by one of the Admiralty recruiting posters in the
window of a Whitehall post office.  It conjured up a vision of a roving,
care-free life . . . of illimitable spaces and great healing winds. . . .
A life of hard living and hard drinking, when a man could forget.</p>
<p id="id00541">But somehow Nosey didn't forget.</p>
<p id="id00542">      *      *      *      *      *</p>
<p id="id00543">The Navy received him without emotion.  They cut his hair and pulled out
his teeth.  They washed and clothed and fed him generously.  He was
taught in a vast echoing drill-shed to recognise and respect authority,
and after six months' preliminary training informed that he was a
Second-class Stoker, and as such drafted to sea in the Battle-Cruiser
Squadron.</p>
<p id="id00544">Here Nosey found himself an insignificant unit among nearly a thousand
barefooted, free-fisted, cursing, clean-shaven men, who smelt perpetually
of soap and damp serge, and comprised the lower-deck complement of a
British battle-cruiser.</p>
<p id="id00545">He worked in an electric-lit, steel tunnel, with red-hot furnaces on one
side, and the gaping mouths of coal caverns on the other.  You reached it
by perpendicular steel ladders descending through a web of hissing steam
pipes and machinery; once across greasy deck-plates and through a maze of
dimly lit alleys, you would find Nosey shovelling coal into the furnaces
under the direction of a hairy-chested individual afflicted, men said, by
religious mania, who sucked pieces of coal as an antidote to chronic
thirst, and spat about him indiscriminately.</p>
<p id="id00546">There were eight-hour intervals in this work, during which Nosey slept or
ate his meals or played a mouth-organ in the lee of one of the
turret-guns on deck, according to the hour of the day.  He slept in a
hammock slung in an electric-lit passage far below the water-line; the
passage was ten feet wide, and there were six hammocks slung abreast
along the entire length of it.</p>
<p id="id00547">He ate his meals in a mess with twenty other men, the mess consisting of
a deal plank covered with oilcloth for a table, and two narrower planks
on either side as seats; there were shelves for crockery against the
ship's side.  All this woodwork was scrubbed and scoured till it was
almost as white as ivory.  Other messes, identical in every respect,
situated three feet apart, ranged parallel to each other as far as the
steel, enamelled bulkheads.  There were twenty men in each mess, and
seventeen messes on that particular mess-deck, and here the members
simultaneously ate, slept, sang, washed their clothes, cursed and
laughed, skylarked or quarrelled all round during the waking hours of
their watch-off.</p>
<p id="id00548">Still Nosey did not forget.</p>
<p id="id00549">      *      *      *      *      *</p>
<p id="id00550">Then came Janie's letter from the Middlesex Hospital.  Janie was in a
"decline."</p>
<p id="id00551">The men who go down into trenches in the firing-line are, if anything,
less heroic than the army of cooks and Janies who descend to spend their
lives in the basement "domestic offices" of Bloomsbury.  Dark and
ill-ventilated in summer, gas-lit and airless throughout the foggy
winter.  Flight upon flight of stairs up which Janie daily toiled a
hundred times before she was suffered to seek the attic she shared with
cook under the slates.  Overwork, lack of fresh air and recreation—all
these had told at last.</p>
<p id="id00552">Nosey availed himself of week-end leave from Portsmouth to journey up to
London, and was permitted an interview with her in the big airy ward.
Neither spoke much; at no time had they been great conversationalists,
and now Janie, more diminutive and angular than ever, lost in the folds
of a flannel nightgown, was content to hold his hand as long as he was
allowed to remain.</p>
<p id="id00553">The past was ignored, or nearly so.  "You didn't orter gone off like
that," said Janie reproachfully.  "But I'm glad you're a sailor.  You
looks beautiful in them clothes.  An' there's prospecks in the Navy."
Poor little Janie: she had "prospecks" herself at last.</p>
<p id="id00554">He left the few flowers he had brought with the sister of the ward when
the time came to leave.  The nurse followed him into the corridor.  "Come
and see her every visiting day you can," she said.  "It does her good and
cheers her.  She often speaks of you."</p>
<p id="id00555">Nosey returned to Portsmouth and his ship.  His mess—the mess-deck
itself—was agog with rumours.  Had he heard the "buzz"?  Nosey had not.
"I bin to London to see a fren'," he explained.</p>
<p id="id00556">Then they told him.</p>
<p id="id00557">The battle-cruiser to which he belonged had been ordered to join the<br/>

Mediterranean Fleet.  That was Monday; they were to sail for Malta on<br/>

Thursday.<br/>
</p>
<p id="id00558">And Janie was dying in the Middlesex Hospital.</p>
<p id="id00559">      *      *      *      *      *</p>
<p id="id00560">The next visiting day found him at Janie's bedside.  But, instead of his
spick-and-span serge suit of "Number Ones" and carefully ironed blue
collar, Nosey wore a rusty suit of "civvies" (civilian clothes).  Instead
of being clean-shaven, an inconsiderable moustache was feeling its way
through his upper lip.</p>
<p id="id00561">"Where's your sailor clothes?" asked Janie weakly.</p>
<p id="id00562">Nosey looked round to reassure himself that they were not overheard.  "I
done a bunk!" he whispered.</p>
<p id="id00563">Janie gazed at him with dismayed eyes.  "Not—not deserted?"</p>
<p id="id00564">Nosey nodded.  "Don't you take on, Janie.  'S only so's I can stay near
you."  He pressed her dry hand.  "I got a barrer—whelks an' periwinkles.
I've saved a bit o' money.  An' now I can stay near you an' come 'ere
visiting days."</p>
<p id="id00565">Janie was too weak to argue or expostulate.  It may have been that she
was conscious of a certain amount of pride in Nosey's voluntary outlawry
for her sake; and she was glad enough to have someone to sit with her on
visiting days and tell her about the outside world she was never to see
again.  She even went back in spirit to the proud days when they walked
out together. . . .  It brought balm to the cough-racked nights and the
weary passage of the days.</p>
<p id="id00566">Then the streets echoed with the cries of paper-boys.  The nurses
whispered together excitedly in their leisure moments; the doctors seemed
to acquire an added briskness.  Once or twice she heard the measured
tramp of feet in the streets below, as a regiment was moved from one
quarters to another.</p>
<p id="id00567">England was at war with Germany, they told her.  But the intelligence did
not interest Janie much at first.  That empires should battle for
supremacy concerned her very little—till she remembered Nosey's late
calling.</p>
<p id="id00568">It was two days before she saw him again, and he still wore his "civvy"
suit.  Janie smiled as he approached the bed, and fumbled with the
halfpenny daily paper that somebody had given her to look at.</p>
<p id="id00569">'"Ere," she whispered, "read that."</p>
<p id="id00570">Nosey bent over and read the lines indicated by the thin forefinger.</p>
<p id="id00571" style="margin-top: 2em">His Majesty the King has been graciously pleased to approve of pardons
being granted to all deserters from the Royal Navy and Marines who
surrender themselves forthwith.</p>
<p id="id00572" style="margin-top: 2em">There was silence in the ward for a moment.  Far below in the street
outside a transport wagon rumbled by.  Janie braced herself for the
supreme act of her life.</p>
<p id="id00573">"You gotter go," said she.</p>
<p id="id00574">Nosey stared at her and then back at the newspaper.  "Not me!" he
retorted, and took possession of her hand.</p>
<p id="id00575">"That's the King's pardon," said Janie, touching the halfpenny news-sheet
with transparent fingers.  "'Tain't no use you comin' 'ere no more, 'cos
I won't see you.  I'll ask 'em at the door not to let you in."</p>
<p id="id00576">Nosey knew that note of indomitable obstinacy in the weak voice.  He
knew, as he sat looking down upon the fragile atom in the bed, that he
could kill her with the pressure of a finger.</p>
<p id="id00577">But there was no way of making Janie go back on her decision once her
mind was made up.  "If there's a war, you orter be fightin'," she added.
"There's prospecks . . ."  Her weak voice was almost inaudible, and the
nurse was coming down the ward towards them.</p>
<p id="id00578">Nosey lifted the hot, dry little claw to his lips.  "If you sez I gotter
go, I'll go," and rose to his feet.</p>
<p id="id00579">"'Course you gotter go.  The King sez so, an' I sez so.  Don't you get
worritin' about me; I'll be all right when you comes 'ome wiv yer medals.
. . ."</p>
<p id="id00580">Nosey caught the nurse's eye and tiptoed out of the ward.  Janie turned
her face to the Valley of the Shadow.</p>
<h2 id="id00581" style="margin-top: 4em">VI</h2>
<h5 id="id00582">AN OFF-SHORE WIND</h5>
<p id="id00583">The circular rim of the fore-top took on a harder outline as the sky
paled at the first hint of dawn.</p>
<p id="id00584">From this elevation it was possible to make out the details of the
ships astern, details that grew momentarily more distinct.  Day,
awakening, found the Battle Fleet steaming in line ahead across a
smooth grey sea.  The smoke from the funnels hung like a long dark
smear against the pearly light of the dawn; but as the pearl changed to
primrose and the primrose to saffron, the sombre streamers dissolved
into the mists of morning.</p>
<p id="id00585">Somewhere among the islands on our starboard bow a little wind awoke
and brought with it the scent of heather and moist earth.  It was a
good smell—just such a smell as our nostrils had hungered for for many
months—and it stirred a host of vagrant memories as it went sighing
past the halliards and shrouds.</p>
<p id="id00586">It was the turn of the Indiarubber Man (with whom I had shared the
night's vigil aloft) to snatch a "stretch off the land" with his back
against the steel side of our erie [Transcriber's note: eyrie?].  He
shifted his position uneasily, and the hood of his duffel-suit fell
back: his face, in the dawning, looked white and tired and unshaven.
Cinders had collected in the folds of the thick garment as wind-blown
snow lies in the hollows of uneven ground.</p>
<p id="id00587">As I stood looking down at him an expression of annoyance passed across
his sleeping countenance.</p>
<p id="id00588">"Any old where——" he said in a clear, decisive voice.  "Down a
rabbit-hole . . ."</p>
<p id="id00589">And I laughed because the off-shore wind had fluttered the same page in
the book of pleasant memories that we both shared.  The petulant
expression passed from his face, and he sank into deeper oblivion,
holding the Thermos flask and binoculars against him like a child
clasping its dolls in its sleep.</p>
<p id="id00590">It was just before we mobilised for the summer—a mobilisation which,
had we but known it, was to last until our book of pleasant memories
was thumbed and dog-eared and tattered with much usage—that the
Indiarubber Man suggested taking a day off and having what he called a
"stamp."  He fetched our ordnance map and spread it on the ward-room
table, and we pored over it most of the evening, sucking our pipes.</p>
<p id="id00591">All Devon is good; and for a while the lanes had called us, winding
from one thatched village to another between their fragrant,
high-banked hedges.  "Think of the little pubs . . ." said the
Indiarubber Man dreamily.  We thought of them, but with the vision came
one of cyclists of the grey-sweater variety, and motorists filling the
air with petrol fumes and dust.</p>
<p id="id00592">There was the river: woodland paths skirting in the evening a world of
silver and grey, across which bats sketched zigzag flights.  Very nice
in the dimpsey light, but stuffy in the daytime.  So the moor had it in
the end.  We would trudge the moor from north to south, never seeing a
soul, and, aided by map and compass, learn the peace of a day spent off
the beaten tracks of man.</p>
<p id="id00593">We had been in the train some time before the Indiarubber Man made his
electrifying discovery.</p>
<p id="id00594">"Where's the map?"  We eyed one another severely and searched our
pockets.  "We were looking at it before I went to get the tickets," he
pursued.  "I gave it to you to fold up."</p>
<p id="id00595">So he had.  I left it on the station seat.</p>
<p id="id00596">At a wayside station bookstall we managed to unearth an alleged
reproduction of the fair face of South Devon to replace the lost map.</p>
<p id="id00597">The Indiarubber Man traced the writhings of several caterpillars with
his pipe-stem.  "These are tors," he explained generously.  After this
we studied the map in silence, vainly attempting to confirm our
recollections of a course marked out the previous evening on an
ordnance survey map.</p>
<p id="id00598">We were both getting slightly confused when, with a screech of brakes,
the train pulled up at the little moorside station that was our
destination by rail.  Sunlight bathed the grey buildings on the
platform and the sleepy village beyond.  From the blue overhead came
the thin, sweet notes of a lark, and as we listened in the stillness we
heard a faint whispering "swish" like the sound of a very distant
reaper.  It was the wind flowing across miles of reeds and grass and
heather from the distant Atlantic.  But it was not until half an hour
later, when we breasted the crest of the great hog-back that stretched
before us like a rampart, that we ourselves met the wind.  It came out
of the west, athwart the sun's rays, a steady rush of warm air; and
with it came the tang of the sea and hint of honey and new-mown hay
that somehow clings to Devon moorland through all the changing seasons.</p>
<p id="id00599">A cluster of giant rocks piled against the sky to our left drew us
momentarily out of our course.  With some difficulty we scrambled up
their warm surfaces, where the lichen clung bleached and russet, and
stood looking out across the rolling uplands of Devon.  Worthier
adventurers would have improved the shining hour with debate as to the
origin of this upflung heap of Nature's masonry.  Had it served
departed Phoenicians as an altar?  Heaven and the archaeologists alone
knew.</p>
<p id="id00600">To the northward the patchwork of plough and green corn, covert and
hamlet commenced at the edge of the railway and stretched undulating
over hill and dale to where a grey smudge proclaimed the sea.</p>
<p id="id00601">South lay the moor, inscrutable and mysterious, dotted with the
monuments of a people forgotten before walls ringed the seven hills of
Rome.  The outlines of tors, ever softening in the distance, led the
eye from rugged crest to misty beacon till, forty miles away, they
dissolved into the same grey haze.</p>
<p id="id00602">The Indiarubber Man pointed a lean, prophetic forefinger to the rolling
south.  "There's Wheatwood," he said.  "Come on."  And so, shouldering
our coats, with the hot sunlight on our right cheeks and the day before
us, we started across Dartmoor.</p>
<p id="id00603">For nearly two hours the tor from which we had started watched with
friendly reassurance over intervening hills; then it dipped out of
sight, and we were conscious of a sudden loneliness in a world of
enigmatic hut-circles, peopled by sheep and peewits.  We were working
across a piece of ground intersected by peat-cuttings, and after half
an hour of it the Indiarubber Man fished out the map and compass from
his pocket.</p>
<p id="id00604">"There ought to be a clump of trees, a hut-circle, and a Roman road
knocking about somewhere.  Can you see anything of them?"</p>
<p id="id00605">I searched the landscape through glasses from my recumbent position in
the heather, but prolonged scrutiny failed to reveal a single tree, nor
was the Roman road startlingly obvious in the trackless waste.  Our map
had proved too clever for us.  In the circumstances there was only one
thing to be done.  With awful calm we folded the sheet, tore it into
little pieces, and hid them in a rabbit-hole.</p>
<p id="id00606">For about five miles after that we kept along a promontory that
shouldered its way across an undulating plain, ringed in the distance
by purple hills; then we sighted our distant landmark—a conical
beacon—that we had been steering for.  We were descending, thigh-deep
in bracken, when the wind bore down to us from a dot against the
skyline of a ridge the tiniest of thin whistles.  A few minutes later a
sheep-dog raced past in the direction of a cluster of white specks.
For a while we watched it, and each lithe, effortless bound, as it
passed upon its quest, struck a responsive chord within us—we who
floundered clumsily among the boulders in our path.</p>
<p id="id00607">But, for all this momentary exhilaration, it seemed a long time later
that we struck the source of the burn which would in time guide us to
our half-way halting place.  To us, who had been nurtured on its broad
bosom,[1] there was something almost pathetic—as in meeting an old
nurse in much reduced circumstances—about this trickle among the peat
and moss.  Lower down, however, it widened, and the water poured over
granite boulders, with a bell-like contralto note, into a succession of
amber pools.</p>
<p id="id00608">There we shed our few garments on the bank, and the moments that
followed, from the first exultant thrill as the water effervesced over
our bodies till we crawled out dripping to dry in the wind and sun,
seemed to hold only gratitude—an immense undefined gratitude to the
Power that held all life.  At its heels came hunger, wonderfully well
defined.</p>
<p id="id00609">Lower down, where the road that stretches like a white ribbon over the
bosom of the moor crosses the river, there is an inn.  I will not name
it: writers of poems and guide-books—worthier penmen all—have done
that.  Besides, quite enough people go there as it is.  We dropped, via
a kine-scented yard and over a seven-foot bank, into the road abreast
the inn door, and here a brake, freighted with tourist folk, brought us
suddenly back to the conventions that everyday life demands.</p>
<p id="id00610">True, we were never fain to cling to these; but, standing there on the
King's high-road, clad in football knickers and thin jerseys, sun-burnt
and dishevelled, we were conscious of a sudden immense embarrassment.
And, in sooth, had we dropped from the skies or been escaping from the
grey prison not far distant, the tenants of the brake could hardly have
been less merciful in their scrutiny or comments.</p>
<p id="id00611">After the clean wind of the moor, the taint of the last meal and
over-clad fellow-beings seemed to cling unpleasantly to the
low-ceilinged room whither we fled, and I do not think we breathed
comfortably again till we had paid our bill and returned to the
sunlight.  Before leaving we inquired the time, and learned it was
nearly four o'clock.</p>
<p id="id00612">One ought to "know the time," it seems, among men's haunts; but, once
out of sight of these, it suffices, surely, to eat when hungry, sleep
when tired, roam as long as daylight and legs will let one—in fine, to
share with the shaggy ponies and browsing sheep a lofty disregard for
all artificial divisions of the earth's journey through space.  And our
joint watch happened at the time to be undergoing repairs in Plymouth.</p>
<p id="id00613">To follow the ramifications of a road gives one no lasting impression
of the surrounding country, but directly a wanderer has to depend on
landmarks as a guide, all his powers of observation quicken.  One
ragged hill-top guided us to another, across valleys scored with the
workings of forgotten tin-mines.  A brook, crooning its queer,
independent moor-song between banks of peat, rambled beside us for some
time.  Then, as if wearying of our company, it turned abruptly and was
lost to view; in the summer stillness of late afternoon we heard it
babbling on long after our ways separated.</p>
<p id="id00614">If the truth be known, I suspect it deemed us dullish dogs.  But we
were tiring—not with the jaded weariness begotten of hard roads, when
the spine aches and knees stiffen; no, a comfortable lassitude was
slackening our joints and bringing thoughts of warm baths and supper.
However, our shadows, valiant fellows, swung along before us across the
rusty bracken with a cheerful constancy, and, encouraged by their
ever-lengthening strides and by the solitude, we even found heart to
lift our voices in song.  Now and again small birds fled upwards with
shrill twitters at our approach, and settled again to resume their
interrupted suppers; but after a while they left for their roosts in
the rowans and sycamores to the south, and rabbits began to show
themselves in the open spaces among the furze.  As if reluctantly, the
perfect day drew to its close.</p>
<p id="id00615">We raced up the flank of a long ridge to keep the setting sun in view,
reaching the crest as it dipped to meet a ragged tor, and sank in a
golden glow.  A little wind, like a tired sigh, ruffled the tops of the
heather, swayed the grass an instant, and was gone.</p>
<p id="id00616">"Ah-h-h!" breathed the Indiarubber Man in the stillness.</p>
<p id="id00617">A thousand feet below us smoke was curling from the thickly wooded
valley.  It was five miles away, but somewhere amid those trees men
brewed and women baked.</p>
<p id="id00618">"Come on," he added tensely.  "Beer!"</p>
<p id="id00619">As we descended into the lowlands a widening circle of night was
stealing up into the sky—the blue-grey and purple of a pigeon's
breast.  A single star appeared in the western sky, intensifying the
peace of the silent moor behind us.  Stumbling through twilit woods and
across fields of young barley, we met a great dog-fox <i>en route</i> for
someone's poultry-run.  He bared his teeth with angry effrontery as he
sheered off and gave us a wide berth across the darkening fields.
Doubtless he claimed his supremacy of hour and place, as did the
sheep-dog that passed us so joyously earlier in the day.  And, after
all, what were we but interlopers from a lower plane!</p>
<p id="id00620">The thirty-odd miles of our ramble reeled up like a tape-measure as we
reached the lane, splashed with moonlight, that led us to the village.
The gateway to every field held a pair of lovers whispering among the
shadows: yet inexplicably they seemed an adjunct of their surroundings
and the faintly bewildering night-scents.  A dog sitting at the gate of
a cottage uttered a short bark as we neared his domain; then, with a
queer grumbling whimper, he came to us across the dust, and perhaps
because—as far as is given to man in his imperfections—we had not
wittingly done evil that day, he slobbered at our hands.</p>
<p id="id00621">In the flagged and wainscotted parlour of the village inn a child
brought us bread and cheese and froth-crested mugs of beer.  While we
ate and drank, she watched us with tranquil interest in violet-coloured
eyes that foretold a sleepless night for some bucolic swain in years to
come.</p>
<p id="id00622">The Indiarubber Man finished his last draught and stood up with a
mighty sigh to loosen his belt.  Then, bending down, he took the
child's flower-like face between his hands:</p>
<p id="id00623">"'Now lettest Thou Thy servant depart in peace,'" he said gravely.<br/>

Beer was ever prone to lend a certain smack of Scripture to his remarks.<br/>
</p>
<p id="id00624">"Surt'nly," said the little maid, all uncomprehending, and ran out to
fetch our reckoning.</p>
<p id="id00625">      *      *      *      *      *</p>
<p id="id00626">The Thermos flask slid with a clatter on to the steel deck of the top,
and the Indiarubber Man opened his eyes.  He yawned and stretched
himself and rose stiffly to his feet.</p>
<p id="id00627">The first rays of the sun were rising out of the sea.  "Hai-yah!"  He
yawned.  "Another bloomin' day. . . .  I was dreaming . . . about . . .
blowed if I can remember what I was dreaming about."  He adjusted the
focus of his glasses and stared out across the North Sea.  "I wonder if
they're coming out to-day."</p>
<p id="id00628">It was the two hundred and seventy-third morning we had wondered that.</p>
<p id="id00629" style="margin-top: 3em">[1] The River Dart.</p>
<h2 id="id00630" style="margin-top: 4em">VII</h2>
<h5 id="id00631">THE DAY</h5>
<p id="id00632">Although it all happened in that dim, remote period of time "Before the<br/>

War," Torps and the First Lieutenant, the Indiarubber Man (who was the<br/>

Lieutenant for Physical Training Duties), the Junior Watchkeeper, and<br/>

others who participated, long afterwards referred to it as "The Day."<br/>
</p>
<p id="id00633">Since then they have seen their own gunfire sink an enemy's ship as a
well-flung brick disposes of an empty tin on the surface of a pond.
The after twelve-inch guns, astride whose muzzles David and Freckles
once soared to the giddy stars, have hurled instantaneous and awful
death across leagues of the North Sea.  The X-ray apparatus, by the
agency of which Cornelius James desired to see right through his own
"tummy," has enabled the Fleet Surgeon to pick fragments of steel out
of tortured bodies, as a conjurer takes things out of a hat.  The
after-cabin, that had witnessed so many informal tea-and-muffin
parties, has been an ether-reeking hospital.</p>
<p id="id00634">Yet these memories grew blurred in time, as mercifully such memories
do.  It was another and more fragrant one that sweetened the grim
winter vigil in the North, when every smudge of smoke on the horizon
might have been the herald of Armageddon.  They were yet to see men die
by scores in the shambles of a wrecked battery, by hundreds on the
shell-torn decks of a ship that sank, fighting gallantly to the last.
And the recollection of what I am about to relate doubtless supplied
sufficient answer to the question that at such times assails the minds
of men.</p>
<p id="id00635">Two who helped in that unforgettable good-night scene on the aft-deck
were destined to add their names to the Roll of Britain's Honour.  It
is not too much to hope that the echo of children's merriment guided
their footsteps through that dark Valley of the Shadow to the peaks of
Eternal Laughter which lie beyond.</p>
<p id="id00636">      *      *      *      *      *</p>
<p id="id00637">It all started during one of those informal tea-parties the Skipper's
Missus sometimes held in the after-cabin.  They were delightful
affairs.  You needn't accept the Invitation if you didn't want to;
there was no necessity to put on your best monkey-jacket if you did.
You were just told to "blow in" if you wanted some tea, and then you
made your own toast, and there was China tea, in a big blue-and-white
pot, that scented the whole cabin.</p>
<p id="id00638">The Skipper's Missus sat by the fire, with her hands linked round her
knees in her habitually graceful and oddly characteristic attitude;
Torps and Jess, those gentle philosophers, occupied the chintz-covered
settee; the A.P. sat on the hearth-rug, cross-legged like a tailor, so
that he could toast and consume the maximum number of muffins with the
minimum amount of exertion; the Junior Watchkeeper, who by his own
admission "went all the bundle on his tea," and the Indiarubber Man,
who was clumsy with a tea-cup, shared the table and a jam-pot, and sat
munching, tranquil-eyed, like a pair of oxen in a stall.</p>
<p id="id00639">The Captain and the First Lieutenant were rummaging through the drawers
of the knee-hole table in search of an ancient recipe of the former's
for manufacturing varnish of a peculiar excellence wherewith to
beautify the corticene on the aft-deck.</p>
<p id="id00640">"How are the children?" asked the Torpedo Lieutenant, helping himself
to milk and Jess to a lump of sugar.  "Out of quarantine yet?"</p>
<p id="id00641">"Yes," replied the youthful mother of Georgina, Jane, and Cornelius
James.  "At last, poor things!  Christmas is such a wretched time to
have measles.  No parties, no Christmas-tree——"</p>
<p id="id00642">The A.P. looked up from the absorbing task of buttering a muffin to his
satisfaction.  "D'you remember the Christmas when you all came on
board—wasn't it a rag?  I broke my glasses because I was a tiger.  I
was that fierce."</p>
<p id="id00643">"And I was chased by the dockyard police all the way from the Admiral
Superintendent's garden with a young fir-tree under my arm.  We had it
for a Christmas-tree in the wardroom.  Do you remember?"</p>
<p id="id00644">They were all old friends, you see, and had served two commissions in
succession with this Captain.</p>
<p id="id00645">"Isn't it rather hard on the <i>Chee-si's</i>?" asked Torps, "being done out
of their parties—no, Jess, three lumps are considered quite enough for
little dogs to consume at one sitting."</p>
<p id="id00646">The Skipper's Missus looked across the cabin at her husband: "Tim, your
tea's getting cold.  Why shouldn't we have a children's party on board
one day next week?  It isn't too late, is it?"</p>
<p id="id00647">"Yes, sir," chimed in the Indiarubber Man.  "A <i>pukka</i> children's
party, with wind-sails for them to slither down and a merry-go-round on
the after-capstan?"</p>
<p id="id00648">The Captain drank his tea thoughtfully; his blue eyes twinkled.  "Let
us have a definition of <i>children</i>, Standish.  I seem to remember a
certain bridesmaid at the Gunnery Lieutenant's wedding of what I
believe is technically called the 'flapper' age——"</p>
<p id="id00649">"Quite right, sir," cut in the Torpedo Lieutenant.  "Our lives were a
misery for weeks afterwards.  He burbled about 'shy flowerets' in his
sleep, sir——"</p>
<p id="id00650">The Indiarubber Man blushed hotly.  "Not 't'll, sir.  They're talking
rot.  She thought I was ninety, and daft at that.  They always do," he
added sighing, the sigh of a sore heart that motley traditionally
covers.</p>
<p id="id00651">"I propose that we have no one older than Georgina or younger than
Cornelius James," suggested the Junior Watchkeeper.  "That limits the
ages to between ten and seven, and then I think Standish's susceptible
heart would be out of danger."</p>
<p id="id00652">"How many children do you propose to turn loose all over the ship?"
inquired the First Lieutenant dourly.  "No one seems to have taken my
paint-work into consideration.  It's all new this week."</p>
<p id="id00653">The Skipper's Missus laughed softly.  "We were so concerned about Mr.<br/>

Standish's heart, Mr. Hornby, that we quite forgot your paint-work.<br/>

Couldn't it be all covered up just for this once?  Besides, they are<br/>

such tiny children . . ."<br/>
</p>
<p id="id00654">There are many skippers' missuses, but only one mother of Georgina,<br/>

Jane, and Cornelius James.<br/>
</p>
<p id="id00655">The First Lieutenant capitulated.</p>
<p id="id00656">"I vote we don't have any grown-ups, either," contributed the Junior
Watchkeeper, "except ourselves.  Mothers and nurses always spoil
children's parties."</p>
<p id="id00657">The mother of Georgina, Jane, and Cornelius James wrung her hands in
mock dismay.  "Oh, but mayn't I come?  I promise not to spoil
anything—I love parties so!"</p>
<p id="id00658">The A.P. rushed in where an angel might have been excused for
faltering.  "Glegg means that you don't count as a grown-up at a
children's party," he explained naively, regarding the Skipper's Missus
through his glasses with dog-like devotion.</p>
<p id="id00659">She laughed merrily.  "You pay a pretty compliment, Mr. Gerrard!"</p>
<p id="id00660">"Double-O" Gerrard reddened and lapsed into bashful silence.</p>
<p id="id00661">"It is agreed, then.  We are to have a children's party, and I may
come.  Won't the children be excited!"</p>
<p id="id00662">"Torps, what are you going to do with them," asked the First
Lieutenant, "besides breaking their necks by pushing them down the
windsails?"  He spoke without bitterness, but as a man who had in his
youth embraced cynicism as a refuge and found the pose easier to retain
than to discard.</p>
<p id="id00663">The Torpedo Lieutenant regarded him severely.  "It's no good adopting
this tone of lofty detachment, Number One.  You're going to do most of
the entertaining, besides keeping my grey hairs company."</p>
<p id="id00664">The First Lieutenant laughed, a sad, hard laugh without any laughter in
it.  "I don't amuse children, I'm afraid.  In fact, I frighten them.
They don't like my face.  No, no——"</p>
<p id="id00665">"Mr. Hornby," interposed the Skipper's Missus reproachfully, "that
isn't quite true, is it?  You know Jane prays for you nightly, and
Corney wouldn't for worlds sleep without that wooden semaphore you made
him——"</p>
<p id="id00666">"I think Hornby would make an admirable Father Neptune," said the<br/>

Captain, considering him mischievously, "with a tow wig and beard——"<br/>
</p>
<p id="id00667">"And my green bath kimono," supplemented the A.P.  "I bought it at
Nagasaki, in the bazaar.  It's got green dragons all over it——"  He
met the First Lieutenant's eye and lapsed into silence again.</p>
<p id="id00668">"Yes!  Yes!  And oyster-shells sewn all over it, and seaweed
trailing . . ."  The Skipper's Missus clapped her hands.  "And
distribute presents after tea.  Oh, Mr. Hornby, <i>wouldn't</i> that be
lovely!"</p>
<p id="id00669">The First Lieutenant took no further part in the discussion.  But late
that night he was observed to select a volume of the "Encyclopaedia
Britannica" (L-N) from the wardroom library, and retire with it to his
cabin.  His classical education had been scanty, and left him in some
doubt as to what might be expected of the son of Saturn and Rhea at a
children's party.</p>
<p id="id00670" style="margin-top: 3em">2</p>
<p id="id00671">"I doubt if any of 'em'll face it," said the First Lieutenant
hopefully, when The Day arrived.  "There's a nasty lop on, and the
glass is tumbling down as if the bottom had dropped out.  It's going to
blow a hurricane before midnight.  Anyhow, they'll all be sick coming
off."</p>
<p id="id00672">The Torpedo Lieutenant was descending the ladder to the picket-boat.
"Bunje and I are going in to look after them.  It's too late to put it
off now."  He glanced at the threatening horizon.  "They'll be all snug
once we get them on board, and this'll all blow over before tea-time."</p>
<p id="id00673">Off went the steamboats, the Torpedo Lieutenant in the picket-boat and
the Indiarubber Man in the steam pinnace, and a tremor of excitement
ran through the little cluster of children gathering at the jetty steps
ashore.</p>
<p id="id00674">"It's awfully rough outside the harbour," announced Cornelius James,
submitting impatiently to his nurse's inexplicable manipulation of the
muffler round his neck.  "I'm never sick, though," he confided to a
small and rather frightened-looking mite of a girl who clung to her
nurse's hand and looked out to the distant ship with some trepidation
in her blue eyes.  "My daddy's a Captain," continued Cornelius James;
"and I'm <i>never</i> sick—are you?"</p>
<p id="id00675">She nodded her fair head.  "Yeth," she lisped sadly.</p>
<p id="id00676">"P'r'aps your daddy isn't a Captain," conceded Cornelius James
magnificently.</p>
<p id="id00677">The maiden shook her head.  "My daddy's an Admiral," was the slightly
disconcerting reply.</p>
<p id="id00678">"I shall steer the boat," asserted Cornelius James presently, by way of
restoring his shaken prestige.</p>
<p id="id00679">"Oh, Corney, you can't," said Jane.  "Casey always lets Georgie steer
father's galley—you know he does.  You're only saying that to show
off."</p>
<p id="id00680">"'M not," retorted Cornelius James.  "I'm a boy: girls can't steer
boats.  'Sides, Georgie'll be sick."</p>
<p id="id00681">"Oh, I hope there'll be a band and dancing," said Georgina rapturously.</p>
<p id="id00682">"That's all you girls think about," snorted a young gentleman of about
her own age, with deep scorn.  "<i>I</i> hope there'll be a shooting
gallery, an' those ras'berry puffs with cream on top. . . ."  His eye
followed the pitching steamboats, fast drawing near.  "Anyhow, I hope
there'll be a shooting gallery. . . .  I say, it's rather rough, isn't
it?"</p>
<p id="id00683">The children, cloaked and muffled in their wraps, watched the boats
buffet their way shoreward in clouds of spray.  The parting injunctions
of nurses and governesses fell on deaf ears.  How could anyone be
expected to listen to prompted rigmaroles about "bread and butter
before cake" and "don't forget to say thank you for asking me" with the
prospect of this brave adventure drawing so near?</p>
<p id="id00684">Georgina, standing on tip-toe with excitement, suddenly emitted a
shrill squeal of emotion.  "Oh! there's Mr. Mainwaring in the first
boat!"</p>
<p id="id00685">"Who's Mr. Mainwaring?" inquired a small girl with a white bow over one
ear, secretly impressed by Georgina's obvious familiarity with the
inspiring figure in the stern sheets of the picket-boat.</p>
<p id="id00686">"<i>Dear</i> Mr. Mainwaring!"  repeated Georgina under her breath, gazing
rapturously at her idol.</p>
<p id="id00687">White Bow repeated her query.</p>
<p id="id00688">"He's—he's Mr. Mainwaring," replied Georgina.  "My Mr. Mainwaring."
Which is about as much information as any young woman may reasonably be
expected to give another who betrays too lively an interest in her
beloved.</p>
<p id="id00689">The Torpedo Lieutenant waved his arm in a gesture of indiscriminate
greeting, and the children responded with a fluttering of hands and
dancing eyes.  The steam pinnace was following hard in the wake of the
picket-boat.</p>
<p id="id00690">Jane, with the far-seeing eye of love, recognised the occupant
instantly.  "There's Mr. Standish," she said.  "<i>My</i> Mr. Standish!"</p>
<p id="id00691">The nurse of Georgina, Jane, and Cornelius James turned to the<br/>

Providence that brooded over a small boy with a freckled face.  "Did<br/>

you ever hear such children?" she asked in an aside.  "<i>Her</i> Mr.<br/>

Standish!  That's the way they goes on all day!"<br/>
</p>
<p id="id00692">The other nodded.  "Mine's like that, too; only it's our ship's
Sergeant of Marines with him."  Master Freckles's choice in the matter
of an idol had evidently not lacked the wise guidance of his nurse.</p>
<p id="id00693">The boats swung alongside in the calm waters of the basin.  The Torpedo
Lieutenant handed his freight of frills and furbelows to the Coxswain's
outstretched arms.  The small boys to a man disdained the helping hand,
but scrambled with fine independence into the stern sheets.</p>
<p id="id00694">"Sit still a minute." The Indiarubber Man counted.  ". . .
Eight—twelve!  Hallo!  Six absentees——  No, Corney, you can't steer,
because I'm going to clap you all below hatches the moment we get
outside."  He raised his voice, hailing the picket-boat.  "All right,
Torps?"  The Torpedo Lieutenant signified that they were all aboard the
lugger, and off they went.</p>
<p id="id00695">The nurses assembled on the end of the jetty waved their handkerchiefs
with valedictory gestures; the wind caught their shrill farewells and
tossed them contemptuously to where the gulls wheeled far overhead.</p>
<p id="id00696">"My!  Isn't it blowing!" said the small boy in freckles, indifferent to
his nurse's lamentations of farewell.  "Look at Nannie's skirts, like a
balloon. . . ."</p>
<p id="id00697">"Yes," agreed the Torpedo Lieutenant gravely.  "It's what's called a
typhoon.  I've only seen one worse, and that was the day I sailed in
pursuit of Bill Blubbernose, the Bargee Buccaneer."</p>
<p id="id00698">Georgina cast him a glance of passionate credence.</p>
<p id="id00699">"Oh!" gasped Freckles, "have you really chased pirates?"  The Torpedo
Lieutenant nodded.  A certain three weeks spent in an open cutter off
the coast of Zanzibar as a midshipman still remained a vivid
recollection.</p>
<p id="id00700">"Tell us about it," said the children, and snuggled closer into the
shelter of the Torpedo Lieutenant's long arms.</p>
<p id="id00701">The steamboats drew near the ship, and in the reeling stern-sheets of
the steam-pinnace the Indiarubber Man stood holding two small figures
by the collars—two small figures whose heads projected far beyond the
lee gunwale.  They were Cornelius James and the young gentleman whose
valiant soul had yearned for shooting galleries and eke raspberry
puffs.  And, horror of horrors! the little girls were laughing.</p>
<p id="id00702">The picket-boat had no casualties to report, and as she went plunging
alongside, the Junior Watchkeeper (in sea-boots at the bottom of the
ladder) heard the Torpedo Lieutenant say:</p>
<p id="id00703">"We cut their noses off and nailed them to the flying jibboom."</p>
<p id="id00704">"And what happened then?" gasped the enthralled Freckles as he was
picked up and hoisted over the rail on to the spray-splashed ladder.</p>
<p id="id00705">"And they all lived happily ever afterwards," murmured the Torpedo
Lieutenant absently.  "Come on, who's next?  One, two, three—on the
next wave.  <i>Hup</i> you go!"</p>
<p id="id00706">At the top of the ladder to greet each small guest stood the mother of
Georgina, Jane, and Cornelius James.  She had lunched on board with her
husband and had spent the early part of the afternoon fashioning a
garment for Father Neptune—</p>
<p id="id00707">  "That the feast might be more joyous,<br/>

  That the time might pass more gaily,<br/>

  And the guests be more contented,"<br/>
</p>
<p id="id00708">quoted the First Lieutenant with his twisted smile, as he tried it on.</p>
<p id="id00709">The quarterdeck had been closed in with an awning and side curtains of
canvas that made all within as snug as any nursery.  The deck had been
dusted with French chalk; bright-coloured flags draped the canvas
walls; the band was whimpering to start.</p>
<p id="id00710">Cornelius James and his fellow sufferer were not long in recovering
from their indisposition; a glass of milk and biscuits soon restored
matters to the normal, and together they sallied forth to sample the
joys that had been prepared for them.</p>
<p id="id00711">There were windsails stretched from the after-bridge to mattresses on
the quarter-deck, down which one shot through the dizzy darkness to end
in a delicious "wump" at the bottom.  The after-capstan was a
roundabout, with its squealing passengers suspended from capstan-bars.
Each grim twelve-inch gun had a saddle strapped round the muzzle, on
which one sat, thrilled and ecstatic, while the great guns rose slowly
to extreme elevation and descended again to mundane levels.</p>
<p id="id00712">There were pennies for the venturesome, to be extracted at great
personal risk from an electric dip; in a dark casemate a green light
shivered in a little glass tube; you placed your hand in front of it,
and on a white screen a skeleton hand appeared in a manner at once
ghostly and delightful.  Cornelius James returned to the quarter-deck
as one who had brushed elbows with the Black Arts.  "But I wish I could
see right froo my own tummy," he confided, sighing, to the First
Lieutenant.</p>
<p id="id00713">The First Lieutenant, however, was rather <i>distrait</i>; he glanced
constantly upwards at the bellying awning overhead and then walked to
the gangway to look out upon the tumbling grey sea and lowering sky.
Once or twice he conferred with a distinguished-looking gentleman who
had not joined in the revels, but, carrying a telescope and wearing a
sword-belt, remained aloof with a rather worried expression.  This was
the Officer of the Watch.</p>
<p id="id00714">"We'll furl it while they're having tea," said the First Lieutenant.
"But how the deuce they're going to get ashore the Lord knows.  I'll
have to hoist in the boats if it gets any worse.  Keep an eye on the
compass and see we aren't dragging."  The Captain came across the deck.</p>
<p id="id00715">"You must furl the awning, Hornby; we're in for a blow."  He looked
round regretfully at the laughing throng of youngsters.</p>
<p id="id00716">"Yes, sir.  And I think we ought to send the children ashore while
there's still time."  As he spoke a wave struck the bottom of the
accommodation ladder and broke in a great cloud of spray.</p>
<p id="id00717">"Too late now, I'm afraid.  They'll have to stay till it moderates.
The wind has backed suddenly.  Get steam on the boat-hoist and hoist in
the boats.  You'd better top-up the ladders.  Pretty kettle of fish,
with my wife and all these children on board."</p>
<p id="id00718" style="margin-top: 3em">3</p>
<p id="id00719">Tea had passed into the limbo of things enjoyed, if not forgotten, and
the guests had gathered in the after-cabin.  "Children!" cried the
mother of Georgina, Jane, and Cornelius James, "a visitor has come on
board to see you!"  As she spoke, a gaunt apparition appeared in the
doorway.  He wore a gilt paper crown, and was dressed in a robe of the
brightest green.  Seaweed hung in festoons from his head and shoulders,
oyster-shells clashed as he walked; in one hand he carried a trident,
and on his back a heavy pannier.  His legs were encased in mighty
boots, a shaggy beard hung down over his chest; his eyes, sombre and
unsmiling, roved over the assembled children.</p>
<p id="id00720">There was a sudden silence: then the small girl with the white bow over
one ear burst into tears.  "Boo-hoo!" she cried.  "Don't like nasty
man," and ran to bury her face in her hostess's gown.  Her fears were
infectious, and symptoms of a general panic ensued.  "I knew it,"
mumbled the visitor despairingly into his beard, "I <i>knew</i> this would
happen."</p>
<p id="id00721">"Children, children, don't be silly—it's only Father Neptune.  He's
got presents for you all.  Won't you go and say how d'you do to him!
He's come all the way from the bottom of the sea."</p>
<p id="id00722">Cornelius James pulled himself together and advanced with outstretched
hand, as befitted the son of a post-captain on board his father's ship.
"I know who you are," he asserted stoutly.  "You're Father Christmas's
brother!"</p>
<p id="id00723">The First Lieutenant hastily accepted this new mythology.  "Quite
right," he replied with gratitude, "quite right!"  Then, as if
realising that something further was required of him, added in a deep
bass voice:</p>
<p id="id00724">"<i>Fee!  Fi!  Fo!  Fum!</i>"</p>
<p id="id00725">White Bow screamed, and even Cornelius James the valiant fell back a
pace.  Matters were beginning to look serious, when the Torpedo
Lieutenant appeared, rather out of breath.  "Sorry we had to rush away
just now, but we had to furl the awning——"  His quick eye took in the
situation at a glance.</p>
<p id="id00726">"Hallo! old chap," he cried, and smote the dejected Father Neptune
on the back.  "I <i>am</i> delighted to see you!  How are all the
mermaids and flying-fish?  Bless my soul! what have you got in this
pannier—dolls . . . lead soldiers, air-guns!  I <i>say</i>——"</p>
<p id="id00727">The children rallied round him as the children of another age must have
rallied round Saint George of England.</p>
<p id="id00728">"Don't like nasty old man," repeated White Bow, considering the First
Lieutenant with dewy eyes.  "Nasty cross old man."  The visitor from
the bottom of the sea fumbled irresolutely with his trident.</p>
<p id="id00729">"Is it really Father Christmas's own brother?" queried a small sceptic,
advancing warily.</p>
<p id="id00730">"Of course it is!  Look here, look at all the things he's brought you,"
and in an undertone to the First Lieutenant, "Buck up, Number One,
don't look so frightened!"  They unslung the pannier and commenced to
unpack the contents; the children gathered round with slowly returning
confidence, and by twos and threes the remainder of the hosts returned
from the upper-deck.</p>
<p id="id00731">"Why aren't they all wet if they've come from the bottom of the sea?"
demanded Freckles the materialist.  "Why isn't Father Christmas's
brother wet?"</p>
<p id="id00732">They looked round in vain.  Father Christmas's brother had vanished.</p>
<p id="id00733">At that moment the Captain entered and sought his wife's eye.  For a
few moments they conferred in an undertone; then she laughed, that
clear confident laugh of hers with which they had shared so many of
life's perplexities.</p>
<p id="id00734">"Children!" she cried, "listen!  Here's an adventure!  We've all got to
sleep on board to-night!"</p>
<p id="id00735">"Oh, mummie!" gasped Georgina with rapture, "how <i>lovely</i>!"  This was a
party, and no mistake.  "Can I sleep in Mr. Mainwaring's cabin?"</p>
<p id="id00736">"And can I sleep in Mr. Standish's cabin?" echoed Jane earnestly.  "And
we needn't go to bed for hours and hours, need we?" chimed in Cornelius
James.</p>
<p id="id00737">"Where are they to sleep?" asked the Captain's wife, turning to the
Torpedo Lieutenant with laughter still in her eyes.   "I never thought
of that.  One always has spare rooms in a house, but a battleship is so
different. . . ."</p>
<p id="id00738">"It's all right," he replied.  "I've arranged all that.  There are a
lot of people ashore: the children can use their cabins, and some of us
can sling in cots for the night.  They'll have to wear our
pyjamas. . . .  But I don't know about baths——"</p>
<p id="id00739">"I think they must have plenary absolution from the tub to-night."  She
glanced at the tiny watch at her wrist.  "Now then, children, half an
hour before bed time: one good romp.  What shall we play?"</p>
<p id="id00740">"Oranges and lemons," said Georgina promptly, and seized the<br/>

Indiarubber Man's hand.<br/>
</p>
<p id="id00741">"I don't know the words," replied her partner plaintively; "I only
'knows the toon,'" as the leadsman said to the Navigator.</p>
<p id="id00742">So the children supplied the words to the men's bass accompaniment; the
Captain and his wife linked hands.  The candle came to light them to
bed; the chopper came to chop off a head; and at the end a grand
tug-of-war terminated with two squealing heaps of humanity in miniature
subsiding on top of the Young Doctor and the A.P.</p>
<p id="id00743">Then they played "Hunt the slipper," at which Torps, with his long
arms, greatly distinguished himself, and "Hide the thimble," at which
Double-O Gerrard, blinking through his glasses straight at the quarry
without seeing it, was hopelessly disgraced.  "General Post" and "Kiss
in the Ring" followed, and quite suddenly the mother of Georgina, Jane,
and Cornelius James decreed it was time for bed, and the best game of
all began.</p>
<p id="id00744">The Captain's wife gathered six pairs of vasty pyjamas over her arm.
"I'll take the girls all together and look after them in my husband's
cabin," she said.  "We'll come along when we're ready.  Will you all
look after the boys?"</p>
<p id="id00745">Freckles fell to the lot of the Junior Watchkeeper; David, specialist
in raspberry puffs, had already attached himself to the Indiarubber
Man.  The A.P. found himself leading off a young gentleman with an
air-gun which he earnestly desired as a bed-fellow.  The remaining two,
red-headed twins who had spent most of the afternoon locked in combat,
were in charge of Torps and the Young Doctor.</p>
<p id="id00746">"Where's Cornelius James?" asked the First Lieutenant suddenly.  "What
a day, what a day!"  A search party was promptly instituted, and the
Captain's son at last discovered forward in the Petty Officers' mess.
Here, seated on the knee of Casey, his father's coxswain, he was being
regaled with morsels of bloater, levered into his willing mouth on the
point of a clasp knife, and washed down by copious draughts of strong
tea out of a basin.</p>
<p id="id00747">"I went to say 'Good night' to Casey," explained the delinquent as he
was being led back to civilisation, "and Casey said I ought to be
hungry after mustering my bag this afternoon.  What does that mean?"</p>
<p id="id00748">"I shouldn't listen to everything Casey tells you," replied the First<br/>

Lieutenant severely.<br/>
</p>
<p id="id00749">"That's what daddy says sometimes," observed Cornelius James.  "But I
like Casey awfully.  Better'n Nannie.  He taught me how to make a
reef-knot, an' I can do semaphore—the whole alphabet . . . nearly."</p>
<p id="id00750">"Here we are," interrupted his harassed mentor, stopping before the
door of his cabin.  "This is where you've got to sleep."  He lifted his
small charge on to the bunk.  "Now then, let's get these shoes
off. . . ."</p>
<p id="id00751">The flat echoed with the voices of children and the sounds of
expostulation.  The Marine sentry (specially selected for the post "on
account of 'im 'avin' a way with children," as the Sergeant-Major had
previously explained to the First Lieutenant) drifted to and fro on his
beat with a smile of ecstatic enjoyment on his faithful R.M.L.I.
features.  For some moments he hovered outside the Junior Watchkeeper's
cabin.  There were indications in the conversation drifting out through
the curtained doorway that all was not well within.  At length Private
Phillips could contain himself no longer.  "Better let me do it, sir.
Bein' a married man, sir, I knows the routine, in a manner o'
speakin' . . ." he said, and plunged into the fray.</p>
<p id="id00752">"Oh, is that you, Phillips?" the relieved voice of the Junior
Watchkeeper was heard to say.  "I can't get the lead of this infernal
rice-string—don't wriggle, Jim—it's rove so taut. . . ."</p>
<p id="id00753">"What '<i>normous</i> pyjamas," said Cornelius James, suffering himself to
be robed in his night-attire.  The operation was conducted with some
difficulty because of the sheathed sword which the visitor had found in
a corner of his host's cabin and refused thereafter to be parted from.
"Have you ever killed anyone with this sword?"  A blustering sea broke
against the ship's side and splashed the glass of the scuttle with
spray.  "Hark at the waves outside!  Can't I have the window open?
Shall I say my prayers to you?"</p>
<p id="id00754">"No," replied the First Lieutenant, with a little wry smile, as the
shadow-fingers of the might-have-been tightened momentarily round his
heart.  "No, I think you'd better wait till Mummie comes."  Shrill
voices and peals of laughter sounded outside.  "Here she is now."</p>
<p id="id00755">He stepped outside, and met the mother of Georgina, Jane, and Cornelius<br/>

James at the head of her flock.<br/>
</p>
<p id="id00756">"Here we are," she exclaimed, laughing.  "But, oh, Mr. Hornby, our
pyjamas are so <i>huge</i>!"</p>
<p id="id00757">"So are ours," said the First Lieutenant, and stooped to gather into
his arms a pathetic object whose pyjama coat of many colours almost
trailed along the deck.  "Cornelius James wants you to go and hear him
say his prayers. . . .  I will find sleeping quarters for this one."</p>
<p id="id00758">Ten minutes later the last child had been swung into its unaccustomed
sleeping quarters; the twins in adjacent cabins had ceased to hurl
shrill defiance at each other; and silence brooded over the flat.  By
the dim light of the police-lamp Private Phillips paced noiselessly up
and down on his beat, and the mother of Georgina, Jane, and Cornelius
James passed softly from cabin to cabin in that gentle meditation
mothers play at bedtime.</p>
<p id="id00759">On her way aft to the after-cabin she met the Torpedo Lieutenant.  "The
children all want to say 'Good night' to you," she said softly.  "Only
don't stay long.  They are so excited, and they'll never go to sleep."
Of all the men on board the Torpedo Lieutenant's heart was perhaps
nearest that of a child.  He tiptoed into the cabin-flat and drew the
curtain of the nearest cabin.</p>
<p id="id00760">"Who's in here?"</p>
<p id="id00761">"Me," said a small voice.  Torps approached the bunk.  "Who's
'me'—Georgina?"</p>
<p id="id00762">"Yes.  Goodnight, Mr. Mainwaring."</p>
<p id="id00763">"Good night, shrimp," replied her idol, submitting to the valediction
of two skinny arms twined tightly round his neck.  "Good night, and
sweet dreams. . . .  No, I can't tell you stories to-night; it's much
too late. . . .  Lie down and go to sleep."</p>
<p id="id00764">In the next cabin, the sound of deep breathing showed that the small
occupant had passed into dreamland.  It was Freckles.  Jane remained
awake long enough to kiss his left eyebrow and was asleep the next
instant.  White Bow also was asleep, and nearly all the remainder
drowsy.  Cornelius James, clasping the First Lieutenant's sword,
however, remained wide-eyed.  "I'm so firsty," he complained
plaintively.</p>
<p id="id00765">"That's called Nemesis, my son," said Torps, and gave him to drink out
of the water-bottle.  "Fank you," said Cornelius James, and sighed, as
children and dogs do after drinking.</p>
<p id="id00766">"Good night, Corney. . . .  Now you must go to sleep and dream of
bloaters.  Oh, aren't you really sleepy?  Well, if you shut your eyes
tight perhaps the dustman won't see you," and switched out the light.
As he was leaving a drowsy voice again spoke out of the darkness.</p>
<p id="id00767">"What did the Buccaneer say when you nailed his nose to the flying
jibboom?"</p>
<p id="id00768">"Please make me a good boy," replied Torps, somewhat at random.</p>
<p id="id00769">"Oh, same's I do," said Cornelius James.</p>
<p id="id00770">"More or less; isn't that sword very uncomfortable?"</p>
<p id="id00771">But no answer came back, for Cornelius James, the hilt of the sword
grasped firmly in two small hands, had passed into the Valhalla of
Childhood.</p>
<h2 id="id00772" style="margin-top: 4em">VIII</h2>
<h5 id="id00773">THE MUMMERS</h5>
<p id="id00774">The sun had not long set, and its afterglow bathed the bay in pink
light.  It was a land-locked harbour, and the surface of the water held
the reflections of the anchored Battle-fleet mirrored to its smallest
detail.  So still was the evening that sounds travelled across the
water with peculiar acute distinctness.</p>
<p id="id00775">On the quarter-deck of the end ship of the lee line a thousand men were
trying to talk in undertones, lighting and relighting pipes, rallying
their friends on distant points of vantage, and humming tunes under
their breath.  The resultant sound was very much like what you would
hear if you placed your ear against a beehive on a summer day, only
magnified a million-fold.</p>
<p id="id00776">The ship's company of a super-Dreadnought, and as many men from other
ships as could be accommodated on board, were gathered on the foremost
part of the quarter-deck, facing aft.  They sat in rows on mess stools,
they were perched astride the after-turret guns, on the shields of the
turrets, clinging to rails, stanchions and superstructure, tier above
tier of men clad in night-clothing—that is to say, in blue jumper and
trousers, with the white V of the flannel showing up each seaman's
bronzed neck and face.  Seamen and marines all wore their caps tilted
comfortably on the backs of their heads, as is the custom of men of
H.M. Navy enjoying their leisure.  Above them all the smoke from a
thousand pipes and cigarettes trembled in a blue haze on the still air
of a summer evening.</p>
<p id="id00777">They were there to witness an impromptu sing-song—a scratch affair
organised at short notice to provide mirth and recreation for a ship's
company badly in need of both.  It was a ship's company hungry for
laughter after endless months of watching and waiting for an enemy that
was biding his time.  Their lungs ached for a rousing, full-throated
chorus ("<i>All</i> together, lads!").  They were simply spoiling to be the
most appreciative audience in the world.</p>
<p id="id00778">On the after-part of the quarter-deck a stage had been hurriedly
constructed—a rude affair of planks and spars that could be disposed
of in a very few moments if necessity arose—that supported a piano.  A
canvas screen, stretched between two stanchions behind the stage, did
duty as scenery, and afforded the performers a "green-room"—for, of
all the ritual connected with appearing upon a stage, the business of
"making-up" lies nearest to the sailor's heart.  Provide him with a
lavish supply of grease-paint, wigs, and the contents of the chaplain's
or the officer of his division's wardrobe, and the success or otherwise
of his turn, when it ultimately comes, matters little to the
sailor-man.  He has had his hour.</p>
<p id="id00779">In front of the stage, a little in advance of the men, rows of chairs
and benches provided sitting accommodation for the officers.  They came
up from dinner, lighting pipes and cigars, a full muster from Wardroom,
Gunroom and Warrant Officers' Mess.  The Captain came last, and his
appearance was the signal for a great outburst of cheering from the
closely packed audience.  They had been waiting for this moment.  It
gave them an opportunity of relieving their pent-up feelings; it also
gave them a chance to show the rest of the Fleet their attitude towards
this Captain of theirs.</p>
<p id="id00780">It was something they were rather proud that the rest of the Fleet
should see.</p>
<p id="id00781">Moreover, the rest of the Fleet, leaning over the forecastle rails and
smoking its evening pipe, did see, and was none the worse for it.</p>
<p id="id00782">A man might have been excused if he betrayed some self-consciousness at
finding himself thus suddenly the cynosure of a thousand-odd pair of
eyes whose owners were doing their best to show him, after their
fashion, that they thought him an uncommonly fine fellow.  The
atmosphere was electrical with this abrupt, boyish ebullition of
feeling.  Yet the Captain's face, as he took his seat, was as composed
as if he were alone in the middle of his own wide moors.  He lit a pipe
and nodded to the Commander beside him to signify that as far as he was
concerned the show could start as soon as they liked.</p>
<p id="id00783">All happy ships own a sing-song party of some sort or another.  It may
be that the singers are content to sit pipe in mouth in the lee of a
gunshield and croon in harmony as the dusk settles down on a day's work
done.  Other ships' companies are more ambitious; the canteen provides
a property-box, and from a flag-decked stage the chosen performers
declaim and clog-dance with all the circumstance of the drama.</p>
<p id="id00784">In days of piping peace, the Operatic and Dramatic Company of this
particular ship had known many vicissitudes.  Under the guidance of a
musically inclined Ship's Steward, it had faced audiences across
impromptu footlights as "The Pale Pink Pierrots," and, as such, had
achieved a meteoric distinction.  But unhappily the Ship's Steward was
partial to oysters, and bought a barrelful at an auction sale ashore.
On the face of things, it appeared a bargain; but the Ship's Steward
neglected to inquire too closely into the antecedents of its contents,
and was duly wafted to other spheres of usefulness.</p>
<p id="id00785">The Chaplain, an earnest man but tone-deaf, rallied the leaderless
troupe of musicians.  During the period of his directorship they were
known to fame as "The Musical Coons."  Musical in that each one wielded
a musical instrument with which he made bold to claim acquaintance,
Coons because they blacked their faces with burnt cork and had
"corner-men."  The corner-men were the weak spots in an otherwise
well-planned organisation.</p>
<p id="id00786">A sailor can be trusted with the integrity of a messmate's honour or
the resources of the mint, conceivably with the key of a brewery
cellar, and justify the confidence reposed in him.  But he cannot be
trusted to be a corner-man, "gagging" with a black face and a pair of
bones.  The Musical Coons dissolved after one performance, during which
the Captain's brow grew black and the Chaplain turned faint, and an
ecstatic ship's company shouted itself hoarse with delirious enjoyment.</p>
<p id="id00787">Thereafter, for a period, the breath of rebuke and disrepute clung to
the songsters; but a ship without a sing-song party is like a dog
without a tail.  A committee of Petty Officers waited upon the First
Lieutenant, as men once proffered Cromwell the Protectorship of
England, lest a worse thing befell them.  The First Lieutenant, with a
reluctance and a full sense of the responsibilities involved, that was
also Cromwellian, finally consented to become the titular head of the
sing-song party.</p>
<p id="id00788">He it was, then, who rose from his chair, holding a slip of paper, and
faced the great bank of faces with one hand raised to enjoin silence.
The cheering redoubled.</p>
<p id="id00789">For perhaps fifteen seconds he stood with raised hand, then he lowered
it and the smile left his eyes.  His brows lowered too.  The cheering
wavered, faltered, died away.  They knew what Number One meant when he
looked like that.</p>
<p id="id00790">"The first item on the programme," he said in his clear voice, "is a
song by Petty Officer Dawson, entitled, 'The Fireman's Daughter,'" and
sat down again amid loud applause.</p>
<p id="id00791">The A.P. rose, hopped on to the stage, and sat down at the piano that
occupied one wing of the stage.  Petty Officer Dawson, who was also the
ship's painter, emerged from behind the canvas screen, coyly wiping his
mouth on the back of his hand.  The piano tinkled out the opening bars
of the song, and the concert began.</p>
<p id="id00792">It was a sad song; the very first verse found the fireman's daughter on
her death-bed.  But the tune was familiar and pleasantly mournful, and,
as the piano thumped the opening bars of the refrain for the second
time, the hundreds of waiting men took it up readily.  The melody
swelled and rose, till the sadness of the theme was somehow overwhelmed
by the sadness that is in the harmony of men's voices singing in the
open air.</p>
<p id="id00793">Petty Officer Dawson was a stout man addicted in daily life to the
inexplicable habit of drying his gold-leaf brush in the few wisps of
hair Nature had left him with.  His role on the occasion of a concert
was usually confined to painting the scenery.  The nation being at war,
and this particular concert held during the effective blockade of an
enemy's empire, scenery was out of the question.  So, as one of the
recognised members of the sing-song party, he sang—with, be it added,
considerable effect.</p>
<p id="id00794">"The next item," announced the First Lieutenant (who knew his audience
better even than they knew him), "is a comic song entitled, 'Hold
tight, Emma!' by Stoker Williams."</p>
<p id="id00795">This was "Taff" Williams, Stoker First-class, comedian tenth-class, and
master of patter unintelligible (mercifully so, perhaps) to any but a
bluejacket audience.  He was a wisp of a man with a pale, beardless
face and small features; incidentally, too, the scrum half of the
ship's Rugby team and the referee's terror.</p>
<p id="id00796">But he was more than this: he was the ship's wag, and so was greeted
with shouts and whistles of approval as he stepped on to the stage
attired in the burlesque counterfeit of an airman's costume.</p>
<p id="id00797">Perhaps you might not have thought his song so very funny after all.
It might even have struck you as vulgar, since it depended for its
humour upon gorgonzola cheese, the eldest son of the German Emperor,
<i>mal-de-mer</i>, and a number of other things not considered amusing in
polite society.  But the sailor's susceptibilities are peculiar: they
were there to enjoy themselves, and again and again a great gust of
laughter swept over the audience as an autumn gale convulses the trees
on the outskirts of a forest.  The singer's topical allusions, sly
incomprehensibilities, he flung about him like bombs that burst in an
unfailing roar of delight among his shipmates.  No wonder they liked
him; and even the padre, who perforce had to knit his brows once or
twice, looked regretful when the last encore was over.</p>
<p id="id00798">Taff Williams's song was succeeded by a duet.  The singers were also
comedians, but of a different calibre.  Some odd freak of Nature had
fashioned them both astoundingly alike in face and frame.  They were
baldish men, short and sturdy, with sandy eyebrows and lashes of so
light a colour as to be almost invisible.  Their countenances were
round and expressionless, and their song, which was called "We are the
Brothers Boo-Hoo!" contained little beyond reiterations of the fact,
interspersed with "steps" of a solemn and intricate nature.</p>
<p id="id00799">Ordinarily their avocations and walks in life were separated by a wide
gulf.  One was a Petty Officer and L.T.O., the other a stoker.  But
Fame recognises no distinctions of class or calling, and circumstances
over which they had little control, the universal decree of the ship's
company in short, drove them on to the stage to face successive
audiences side by side as The Brothers Boo-Hoo.  Neither dreamed of
appearing there without the other, although off it, save for a few
grave rehearsals, they rarely met.  They were not vocalists, but they
bowed to popular demand, preserving their stolid, immobile demeanours,
and sang in accents sternly and unintelligibly Gaelic.</p>
<p id="id00800">Their turn over, the First Lieutenant announced a juggling display by
Boy Buggins.  Boy Buggins appeared, very spick and span in a brand new
suit of Number Threes, and proceeded to juggle with canteen eggs,
Indian clubs and mess crockery (while the caterer of his mess held his
breath to the verge of apoplexy) in a manner quite bewildering.</p>
<p id="id00801">The Captain took his pipe out of his mouth and leaned towards the
Commander.  "Where did the lad pick up these antics?" he inquired,
smiling.</p>
<p id="id00802">The Commander shook his head.  "I don't know, sir.  Probably in a
circus."</p>
<p id="id00803">As a matter of fact, Boy Buggins did start life (as far as his memory
carried him) in grubby pink tights and spangles.  But he followed in
the train of no circus; it was in front of public-houses in a district
of London where such pitches recurred with dreary frequency that he cut
capers on a strip of carpet.  He visited them nightly in the company of
a stalwart individual who also wore pink tights.  After each
performance the stalwart one ordained an interval for refreshment.  On
good days he used to reach home very much refreshed indeed.</p>
<p id="id00804">They called it home (it was a cellar) because they slept there; and as
often as not a thin woman with tragic eyes was there waiting for them.
She used to hold out her hand with a timid, shamed gesture, and there
was money in it which the man took.  If he had had a good day or she a
bad one—it was always one or the other—the stalwart one beat the
woman, or, in his own phraseology, "put it acrost" her.  But ultimately
he had one good day too many, or else he felt unusually stalwart, for
the woman lay motionless in the corner of the cellar where she was
flung, and wouldn't answer when he had finished kicking her.</p>
<p id="id00805">The police took the stalwart one away to swing for it, and "the parish"
took the thin woman away in a deal box.  Boy Buggins passed, via an
industrial training ship, into the Royal Navy, and earned the
Distinguished Conduct Medal before this particular sing-song had passed
out of the minds of those who were present at it.</p>
<p id="id00806">One must conclude that all these things were, as the Arabs say, on his
forehead.</p>
<p id="id00807">"Private Mason, R.M.L.I.—Concertina Solo!"</p>
<p id="id00808">A great burst of laughter and cheering broke out from the sailors, and
redoubled as a private of Marines, holding a concertina in his gnarled
fists, walked on to the stage.  Even the officers put their hands up to
smile behind them; one or two nearest the First Lieutenant leaned over
and patted him on the back as if he had achieved something.</p>
<p id="id00809">The whole audience, officers and men, were evidently revelling in some
tremendous secret reminiscence conjured up by the appearance of this
private of Marines.  Yet, as he stood there, fingering the keys of his
instrument, waiting for the uproar to subside, there was little about
him to suggest high humour.  He was just a thin, rather
delicate-looking man with a grizzled moustache and dreamy eyes fixed on
vacancy.  His claim to notoriety, alas, lay in more than his
incomparable music.  Human nature at its best is a frail thing.  But
human nature, as typified by Private Mason, was very frail.  Apart from
his failing he was a valuable asset to the sing-song party; but,
unhappily, it required all the resources and ingenuity of its promoters
to keep Private Mason sober on the night of an entertainment.</p>
<p id="id00810">When and how he acquired the wherewithal to wreck the high hopes of the
reigning stage manager was a mystery known to him alone.  His messmates
drained their tots at dinner with conscientious thoroughness, and his
into the bargain, striving together less in the cause of temperance
than from a desire that he should for once do himself and his
concertina (of which he was a master) justice.</p>
<p id="id00811">Yet, his turn announced, on the last occasion of a concert before the
war, the curtain rose upon an empty stage.  The Carpenter's party
happened upon him, as archaeologists might excavate a Sleeping Bacchus
or a recumbent Budda, in the process of dismantling the stage.  Private
Mason was underneath it, breathing stertorously, a smile of beatific
contentment on his worn features, his head pillowed on his concertina.</p>
<p id="id00812">The Fleet Surgeon subsequently missed a large-sized bottle of
eau-de-Cologne from his cabin, which he was bringing home from
Gibraltar as a present for his wife.  The discovery of the loss
assisted him in his diagnosis of the case.</p>
<p id="id00813">Silence fell on the audience at length, and the concertina solo began.
As has been indicated, Private Mason could play the concertina.  In his
rather tremulous hands it was no longer an affair of leather and wood
(or of whatever material concertinas are constructed), but a living
thing that laughed and sobbed, and shook your soul like the Keening.
It became a yearning, passionate, exultant daughter of Music that
somehow wasn't quite respectable.</p>
<p id="id00814">And when he had finished, and passed his hand across his moist forehead
preparatory to retiring from the stage, they shouted for more.</p>
<p id="id00815">"Church bells, Nobby!" cried a hundred voices.  "Garn, do the church
bells!"  So he did the church bells, as the wind brings the sound
across the valley on a summer evening at home, wringing his shipmates'
sentimental heartstrings to the limit of their enjoyment.</p>
<p id="id00816">"Strewth!" ejaculated a bearded member of the audience when the turn
was over, relighting his pipe with a hand that shook.  "I 'ear Nobby
play that at the Canteen at Malta, time Comman'er-in-Chief an' 'is
Staff was there—Comman'er-in-Chief, so 'elp me, 'e sob' like a woman.
. . ."</p>
<p id="id00817">The reminiscence may not have been in strict accordance with the truth,
but, even considered in the light of fiction, it was a pretty testimony
to Private Mason's art.</p>
<p id="id00818">The last turn of the evening came an hour later when the slightly
embarrassed Junior Watchkeeper stepped on to the stage.  His appearance
was the signal for another great outburst of enthusiasm from the men.
He was not perhaps more of a favourite with them than any of his
brethren seated on the chairs below; but he was an officer, obviously
not at ease on a concert stage, only anxious to do his bit towards
making the evening a success.  They realised it on the instant, with
the readiness of seamen to meet their officers half-way when the latter
are doing something they evidently dislike to help the common weal.
They knew the Junior Watchkeeper didn't want to sing, and they cared
little what he sang about, but they cheered him with full-throated
affection as he stood gravely facing them, waiting for a lull.</p>
<p id="id00819">It is just this spirit, of which so much has been imperfectly conveyed
to the layman—is, in fact, not comprehended in its entity by
outsiders—which is called for want of a better term "sympathy between
officers and men."  It is a bond of mutual generosity and loyalty,
strong as steel, more formidable to an enemy than armaments;
strengthened by monotony and a common vigil, it thrives on hardships
shared, and endures triumphant, as countless tales shall tell, down to
the gates of Death.</p>
<p id="id00820">The Junior Watchkeeper's song was an old one—one that had stirred the
hearts of sailors no longer even memories with his audience.  He sang
simply and tunefully in the strong voice of one who knew how to pitch
an order in the open air.  When it was finished, he acknowledged the
tumultuous applause by a stiff little bow and retreated, flushing
slightly.  The sing-song was over.</p>
<p id="id00821">The officers were rising from their chairs, the A.P. at the piano was
looking towards the Commander for permission to crash out the opening
bars of the Anthem that would swing the audience as one man to its
feet.  At that moment a Signalman threaded his way through the chairs
and saluted the Captain.</p>
<p id="id00822">The latter took the signal-pad extended to him, and read the message.
Then he turned abruptly to the audience, his hand raised to command
silence.  The last of the warm glow that lingered long in the northern
summer twilight lit his strong, fine face as he faced his men.  There
was a great hush of expectancy.</p>
<p id="id00823">"Before we pipe down," he said, "I want to read you a message that has
just come from the Commander-in-Chief.  'One of our destroyers engaged
and sank by gunfire two of the enemy's destroyers this afternoon.'"</p>
<p id="id00824">A great roar of cheering greeted the curt message.  The listening fleet
took it up, and in the stillness of the land-locked harbour the volume
of sound reverberated, savagely and triumphantly exultant.</p>
<p id="id00825">The hills ashore caught the echo and tossed it sleepily to and fro.</p>
<p id="id00826">Then, flushed with excitement and hoarse with shouting, they sang the<br/>

National Anthem to a close.<br/>
</p>
<p id="id00827">Altogether, it was a very noisy evening.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 7877760448125672173_25749-8-0.txt.vue?670fc6b2